import React, { useState, useEffect } from "react";
import {
  Tree,
  Card,
  Button,
  Modal,
  Form,
  Select,
  Typography,
  Tag,
  Tooltip,
  Divider,
  Row,
  Col,
  Alert,
  Dropdown,
  Menu,
  Spin,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  FolderOutlined,
  FileOutlined,
  MoreOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { supabase } from "../../lib/supabase";
import { message } from "antd";
import { useBookingTypes } from "../../hooks/useBookingTypes";

const { Title, Text } = Typography;
const { Option } = Select;

interface PricingComponent {
  id: number;
  name: string;
  description?: string;
  component_type: string;
  calculation_type: string;
  behavior_type?: string;
}

interface Port {
  id: number;
  name: string;
  country?: string;
}

interface TransportType {
  id: number;
  name: string;
  abbreviation?: string;
}

// Utility function to group array by key
const groupBy = (
  array: any[],
  keyFn: (item: any) => string
): Record<string, any[]> => {
  return array.reduce((groups, item) => {
    const key = keyFn(item);
    if (!groups[key]) {
      groups[key] = [];
    }
    groups[key].push(item);
    return groups;
  }, {} as Record<string, any[]>);
};

interface PricingNode {
  id: string;
  key: string;
  title: React.ReactNode;
  children?: PricingNode[];
  isLeaf?: boolean;
  nodeType: "folder" | "specific";
  selectable?: boolean; // For header nodes that should just expand/collapse
  criteria: {
    transportType?: { id: number; name: string };
    originPort?: { id: number; name: string };
    destinationPort?: { id: number; name: string };
    bookingType?: { id: number; name: string };
    incoterm?: { id: number; name: string };
    containerSize?: { id: number; name: string };
  };
  components: Array<{
    id: string;
    name: string;
    value: number;
    currency: string;
    appliesEverywhere: boolean;
    componentType?: string;
    calculationType?: string;
    includedProducts?: string;
    extraProductSOC?: string;
  }>;
  inheritedTotal: number;
  ownTotal: number;
  finalTotal: number;
}

interface TreePricingTabProps {
  // Future props can be added here
}

interface DatabaseRule {
  id: number;
  name: string;
  transport_type: number | null;
  origin_port: number | null;
  destination_port: number | null;
  shipment_type: number | null;
  incoterm: number | null;
  transport_type_name: string | null;
  origin_port_name: string | null;
  destination_port_name: string | null;
}

interface DatabaseComponent {
  pricing_rule_id: number;
  value: string;
  currency: string;
  component_id: number;
  component_name: string;
  component_type: string;
  calculation_type: string;
  behavior_type: string | null;
  rule_name: string;
}

const TreePricingTab: React.FC<TreePricingTabProps> = () => {
  const [treeData, setTreeData] = useState<PricingNode[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [selectionModalVisible, setSelectionModalVisible] = useState(false);
  const [selectionModalType, setSelectionModalType] = useState<
    "transport" | "booking" | "incoterm" | "origin" | "destination" | null
  >(null);
  const [selectionModalParent, setSelectionModalParent] = useState<any>(null);
  const [editingNode, setEditingNode] = useState<PricingNode | null>(null);
  const [form] = Form.useForm();
  const [selectedNodeData, setSelectedNodeData] = useState<PricingNode | null>(
    null
  );
  const [originPorts, setOriginPorts] = useState<Port[]>([]);
  const [destinationPorts, setDestinationPorts] = useState<Port[]>([]);
  const [transportTypes, setTransportTypes] = useState<TransportType[]>([]);
  const [incoterms, setIncoterms] = useState<any[]>([]);
  const [availableComponents, setAvailableComponents] = useState<
    PricingComponent[]
  >([]);
  const [selectedComponents, setSelectedComponents] = useState<Set<string>>(
    new Set()
  );
  const [helpModalVisible, setHelpModalVisible] = useState(false);
  const [rulesData, setRulesData] = useState<any[]>([]);

  // Debouncing for value updates
  const [updateTimeouts, setUpdateTimeouts] = useState<
    Map<string, NodeJS.Timeout>
  >(new Map());

  // Use the real booking types hook
  const { bookingTypes, loading: bookingTypesLoading } = useBookingTypes();

  // NEW: Enhanced render function with intuitive "Add" buttons at each level
  function renderNodeTitle(
    name: string,
    type: "folder" | "specific",
    inherited: number,
    own: number,
    total: number,
    level?: string,
    parentCriteria?: any
  ) {
    const isFolder = type === "folder";
    const isHeaderSection =
      name.includes("Transport Type") || name.includes("Specific Transport");

    // Check if this is an actionable node that can have pricing components
    const isActionable =
      (type === "specific" &&
        (level === "origin-port" ||
          level === "destination-port" ||
          (level === "incoterm" && !name.includes("Incoterms")) ||
          (level === "container-size" && !name.includes("Container")))) ||
      (type === "folder" &&
        (level === "transport-type" || // Airfreight, Seafreight
          name.includes("Any Transport Type") || // Global costs
          name.includes("Any Destination Port") ||
          name.includes("Any Origin Port") ||
          name.includes("Any Incoterm")));

    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          padding: isHeaderSection ? "8px 12px" : "4px 8px",
          borderRadius: "6px",
          backgroundColor: isHeaderSection
            ? "#e6f7ff"
            : isFolder
            ? "#f8f9fa"
            : "#fff",
          border: isHeaderSection
            ? "1px solid #91d5ff"
            : isFolder
            ? "1px solid #e9ecef"
            : "1px solid #f0f0f0",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {isActionable && (
            <span
              style={{ color: "#52c41a", fontWeight: "bold", fontSize: "14px" }}
            >
              💰
            </span>
          )}
          {isFolder ? (
            <FolderOutlined
              style={{ color: isHeaderSection ? "#0050b3" : "#1890ff" }}
            />
          ) : (
            <FileOutlined style={{ color: "#52c41a" }} />
          )}
          <Text
            strong={isFolder || isHeaderSection}
            style={{
              color: isHeaderSection ? "#0050b3" : undefined,
            }}
          >
            {name}
          </Text>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {/* Pricing tags */}
          {inherited > 0 && (
            <Tooltip title="Inherited from parent rules">
              <Tag color="blue">+€{inherited}</Tag>
            </Tooltip>
          )}
          {own > 0 && (
            <Tooltip title="Components in this rule">
              <Tag color="green">€{own}</Tag>
            </Tooltip>
          )}
          {total > 0 && (
            <Text
              strong
              style={{
                color: "#52c41a",
                minWidth: "60px",
                textAlign: "right",
                fontSize: "12px",
              }}
            >
              €{total}
            </Text>
          )}

          {/* INTUITIVE ADD BUTTONS based on level */}
          {renderAddButtons(level, parentCriteria, name)}

          {/* Edit/Delete for specific rules only */}
          {!isHeaderSection && (
            <Dropdown
              overlay={renderNodeMenu(name)}
              trigger={["click"]}
              placement="bottomRight"
            >
              <Button type="text" size="small" icon={<MoreOutlined />} />
            </Dropdown>
          )}
        </div>
      </div>
    );
  }

  // NEW: Render intuitive "Add" buttons based on the tree level with availability checking
  function renderAddButtons(
    level?: string,
    parentCriteria?: any,
    currentName?: string
  ) {
    const buttonStyle = {
      fontSize: "11px",
      height: "24px",
      padding: "0 8px",
    };

    switch (level) {
      case "transport-header":
        const availableTransportTypes = getAvailableTransportTypes();
        if (availableTransportTypes.length === 0) {
          return (
            <Tooltip title="All transport types already have rules">
              <Button
                type="default"
                size="small"
                disabled
                icon={<PlusOutlined />}
                style={buttonStyle}
              >
                All Rules Created
              </Button>
            </Tooltip>
          );
        }
        return (
          <Button
            type="primary"
            size="small"
            icon={<PlusOutlined />}
            style={buttonStyle}
            onClick={(e) => {
              e.stopPropagation();
              handleAddTransportTypeRule();
            }}
          >
            Add Transport Rule
          </Button>
        );

      case "transport-type":
        return null; // Booking type button moved to booking types header

      case "booking-type":
        if (!parentCriteria?.transportType?.id) return null;

        const availableBookingTypes = getAvailableBookingTypes(
          parentCriteria.transportType.id
        );
        if (availableBookingTypes.length === 0) {
          return (
            <Tooltip title="All booking types already have rules for this transport type">
              <Button
                type="default"
                size="small"
                disabled
                icon={<PlusOutlined />}
                style={buttonStyle}
              >
                All Rules Created
              </Button>
            </Tooltip>
          );
        }
        return (
          <Button
            type="primary"
            size="small"
            icon={<PlusOutlined />}
            style={buttonStyle}
            onClick={(e) => {
              e.stopPropagation();
              handleAddIncoterm(parentCriteria);
            }}
          >
            Add Incoterm
          </Button>
        );

      case "incoterm":
        if (
          !parentCriteria?.transportType?.id ||
          !parentCriteria?.bookingType?.id
        )
          return null;

        const availableIncoterms = getAvailableIncoterms(
          parentCriteria.transportType.id,
          parentCriteria.bookingType.id
        );
        if (availableIncoterms.length === 0) {
          return (
            <Tooltip title="All incoterms already have rules for this combination">
              <Button
                type="default"
                size="small"
                disabled
                icon={<PlusOutlined />}
                style={buttonStyle}
              >
                All Rules Created
              </Button>
            </Tooltip>
          );
        }
        return (
          <Button
            type="primary"
            size="small"
            icon={<PlusOutlined />}
            style={buttonStyle}
            onClick={(e) => {
              e.stopPropagation();
              handleAddOriginPort(parentCriteria);
            }}
          >
            Add Origin Port
          </Button>
        );

      case "origin-port":
        if (
          !parentCriteria?.transportType?.id ||
          !parentCriteria?.bookingType?.id ||
          !parentCriteria?.incoterm?.id
        )
          return null;

        const availableOriginPorts = getAvailableOriginPorts(
          parentCriteria.transportType.id,
          parentCriteria.bookingType.id,
          parentCriteria.incoterm.id
        );
        if (availableOriginPorts.length === 0) {
          return (
            <Tooltip title="All origin ports already have rules for this combination">
              <Button
                type="default"
                size="small"
                disabled
                icon={<PlusOutlined />}
                style={buttonStyle}
              >
                All Rules Created
              </Button>
            </Tooltip>
          );
        }
        return (
          <Button
            type="primary"
            size="small"
            icon={<PlusOutlined />}
            style={buttonStyle}
            onClick={(e) => {
              e.stopPropagation();
              handleAddDestinationPort(parentCriteria);
            }}
          >
            Add Destination Port
          </Button>
        );

      case "destination-port":
        if (
          !parentCriteria?.transportType?.id ||
          !parentCriteria?.bookingType?.id ||
          !parentCriteria?.incoterm?.id ||
          !parentCriteria?.originPort?.id
        )
          return null;

        const availableDestinationPorts = getAvailableDestinationPorts(
          parentCriteria.transportType.id,
          parentCriteria.bookingType.id,
          parentCriteria.incoterm.id,
          parentCriteria.originPort.id
        );
        if (availableDestinationPorts.length === 0) {
          return (
            <Tooltip title="All destination ports already have rules for this combination">
              <Button
                type="default"
                size="small"
                disabled
                icon={<PlusOutlined />}
                style={buttonStyle}
              >
                All Rules Created
              </Button>
            </Tooltip>
          );
        }
        return (
          <Button
            type="primary"
            size="small"
            icon={<PlusOutlined />}
            style={buttonStyle}
            onClick={(e) => {
              e.stopPropagation();
              handleAddDestinationPort(parentCriteria);
            }}
          >
            Add Rule
          </Button>
        );

      default:
        return null;
    }
  }

  // NEW: Specific handler functions for each type of rule
  const handleAddTransportTypeRule = () => {
    console.log("Add Transport Type clicked"); // Debug
    console.log("Transport types:", transportTypes); // Debug
    setSelectionModalType("transport");
    setSelectionModalParent(null);
    setSelectionModalVisible(true);
    console.log("Modal should be visible now"); // Debug
  };

  // Handle creating a rule with the selected option
  const handleCreateRule = async (type: string, data: any) => {
    try {
      setLoading(true);
      setSelectionModalVisible(false);

      let ruleName = "";
      let insertData: any = {
        transport_type: null,
        shipment_type: null,
        incoterm: null,
        origin_port: null,
        destination_port: null,
        valid_from: new Date().toISOString(),
        is_active: true,
      };

      switch (type) {
        case "transport":
          ruleName = `${data.transportType.name} Rules`;
          insertData.transport_type = data.transportType.id; // null for "All Transport Types"
          break;
        case "booking":
          ruleName = `${data.transportType?.name || "Transport"} - ${
            data.bookingType.name
          }`;
          insertData.transport_type = data.transportType?.id || null;
          insertData.shipment_type = data.bookingType.id;
          break;
        case "incoterm":
          const transportName = data.transportType?.name || "Transport";
          const bookingName = data.bookingType?.name || "Booking";
          ruleName = `${transportName} - ${bookingName} - ${data.incoterm.name}`;
          insertData.transport_type = data.transportType?.id || null;
          insertData.shipment_type = data.bookingType?.id || null;
          insertData.incoterm = data.incoterm.id;
          break;
        case "origin":
          ruleName = `${data.transportType?.name || "Transport"} - ${
            data.bookingType?.name || "Booking"
          } - ${data.incoterm?.name || "Incoterm"} - ${data.originPort.name}`;
          insertData.transport_type = data.transportType?.id || null;
          insertData.shipment_type = data.bookingType?.id || null;
          insertData.incoterm = data.incoterm?.id || null;
          insertData.origin_port = data.originPort.id;
          break;
        case "destination":
          ruleName = `${data.transportType?.name || "Transport"} - ${
            data.bookingType?.name || "Booking"
          } - ${data.incoterm?.name || "Incoterm"} - ${
            data.originPort?.name || "Origin"
          } to ${data.destinationPort.name}`;
          insertData.transport_type = data.transportType?.id || null;
          insertData.shipment_type = data.bookingType?.id || null;
          insertData.incoterm = data.incoterm?.id || null;
          insertData.origin_port = data.originPort?.id || null;
          insertData.destination_port = data.destinationPort.id;
          break;
      }

      insertData.name = ruleName;

      const { data: result, error } = await supabase
        .from("pricing_rules")
        .insert(insertData)
        .select()
        .single();

      if (error) throw error;

      message.success(`Added new ${type} rule - click to edit details`);

      // Preserve expanded keys before reloading
      const currentExpandedKeys = [...expandedKeys];

      // Reload data to show the new rule
      await loadPricingData();

      // Restore expanded keys after reload
      setExpandedKeys(currentExpandedKeys);
    } catch (error) {
      console.error(`Error adding ${type} rule:`, error);
      message.error(`Failed to add ${type} rule`);
    } finally {
      setLoading(false);
    }
  };

  // Removed handleAddBookingType - now using selection modal approach

  const handleAddIncoterm = (parentCriteria: any) => {
    setSelectionModalType("incoterm");
    setSelectionModalParent(parentCriteria);
    setSelectionModalVisible(true);
  };

  const handleAddOriginPort = (parentCriteria: any) => {
    setSelectionModalType("origin");
    setSelectionModalParent(parentCriteria);
    setSelectionModalVisible(true);
  };

  const handleAddDestinationPort = (parentCriteria: any) => {
    setSelectionModalType("destination");
    setSelectionModalParent(parentCriteria);
    setSelectionModalVisible(true);
  };

  // Render context menu for each node
  function renderNodeMenu(nodeName: string) {
    const handleMenuClick = ({ key }: { key: string }) => {
      switch (key) {
        case "delete":
          handleDeleteRule(selectedNodeData);
          break;
      }
    };

    return (
      <Menu onClick={handleMenuClick}>
        <Menu.Item key="delete" icon={<DeleteOutlined />} danger>
          Delete
        </Menu.Item>
      </Menu>
    );
  }

  // Handler functions for Quick Actions

  const handleDeleteRule = async (node: PricingNode | null) => {
    if (!node) {
      message.warning("Please select a rule to delete");
      return;
    }

    // Check if this is a database rule (has real ID) or just a calculated folder
    if (!node.id.startsWith("rule-") && node.components.length === 0) {
      message.warning("This is a folder node with no components to delete");
      return;
    }

    const ruleName = node.id.startsWith("rule-")
      ? `Rule ${node.id.replace("rule-", "")}`
      : generateRuleName(node.criteria);

    Modal.confirm({
      title: "Delete Rule",
      content: `Are you sure you want to delete "${ruleName}"? This will remove all pricing components associated with this rule.`,
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      async onOk() {
        try {
          setLoading(true);

          if (node.id.startsWith("rule-")) {
            // Delete existing database rule
            const ruleId = parseInt(node.id.replace("rule-", ""));

            // First delete all pricing values for this rule
            const { error: valuesError } = await supabase
              .from("pricing_values")
              .delete()
              .eq("pricing_rule_id", ruleId);

            if (valuesError) throw valuesError;

            // Then delete the rule itself
            const { error: ruleError } = await supabase
              .from("pricing_rules")
              .delete()
              .eq("id", ruleId);

            if (ruleError) throw ruleError;

            message.success("Rule deleted successfully");
          } else {
            // For folder nodes, find and delete the matching rule based on criteria
            let query = supabase
              .from("pricing_rules")
              .select("id")
              .eq("is_active", true);

            // Add conditions using proper null handling
            if (node.criteria.transportType?.id) {
              query = query.eq(
                "transport_type",
                node.criteria.transportType.id
              );
            } else {
              query = query.is("transport_type", null);
            }

            if (node.criteria.bookingType?.id) {
              query = query.eq("shipment_type", node.criteria.bookingType.id);
            } else {
              query = query.is("shipment_type", null);
            }

            if (node.criteria.incoterm?.id) {
              query = query.eq("incoterm", node.criteria.incoterm.id);
            } else {
              query = query.is("incoterm", null);
            }

            if (node.criteria.originPort?.id) {
              query = query.eq("origin_port", node.criteria.originPort.id);
            } else {
              query = query.is("origin_port", null);
            }

            if (node.criteria.destinationPort?.id) {
              query = query.eq(
                "destination_port",
                node.criteria.destinationPort.id
              );
            } else {
              query = query.is("destination_port", null);
            }

            const { data: existingRules, error: searchError } = await query;

            if (searchError) throw searchError;

            if (existingRules && existingRules.length > 0) {
              const ruleId = existingRules[0].id;

              // Delete all pricing values for this rule
              const { error: valuesError } = await supabase
                .from("pricing_values")
                .delete()
                .eq("pricing_rule_id", ruleId);

              if (valuesError) throw valuesError;

              // Delete the rule itself
              const { error: ruleError } = await supabase
                .from("pricing_rules")
                .delete()
                .eq("id", ruleId);

              if (ruleError) throw ruleError;

              message.success(
                "Rule and all its components deleted successfully"
              );
            } else {
              message.warning("No matching rule found to delete");
            }
          }

          // Clear selection and reload data
          setSelectedKeys([]);
          setSelectedNodeData(null);
          await loadPricingData();
        } catch (error) {
          console.error("Error deleting rule:", error);
          message.error("Failed to delete rule");
        } finally {
          setLoading(false);
        }
      },
    });
  };

  // Helper function to check if a rule combination already exists
  const ruleExists = (criteria: any, allRules: any[]) => {
    return allRules.some((rule) => {
      return (
        (criteria.transportType?.id || null) ===
          (rule.transport_type || null) &&
        (criteria.bookingType?.id || null) === (rule.shipment_type || null) &&
        (criteria.incoterm?.id || null) === (rule.incoterm || null) &&
        (criteria.originPort?.id || null) === (rule.origin_port || null) &&
        (criteria.destinationPort?.id || null) ===
          (rule.destination_port || null)
      );
    });
  };

  // Helper functions to get available options at each level
  const getAvailableTransportTypes = () => {
    if (!rulesData || rulesData.length === 0) return transportTypes;
    const existingTransportTypes = new Set(
      rulesData.map((rule: any) => rule.transport_type).filter(Boolean)
    );
    return transportTypes.filter((tt) => !existingTransportTypes.has(tt.id));
  };

  const getAvailableBookingTypes = (transportTypeId: number | null) => {
    if (!rulesData || rulesData.length === 0) return bookingTypes;
    const existingCombinations = new Set();
    rulesData.forEach((rule: any) => {
      if (rule.transport_type === transportTypeId) {
        existingCombinations.add(
          `${rule.transport_type}-${rule.shipment_type}`
        );
      }
    });

    return bookingTypes.filter(
      (bt) => !existingCombinations.has(`${transportTypeId}-${bt.id}`)
    );
  };

  const getAvailableIncoterms = (
    transportTypeId: number | null,
    bookingTypeId: number | null
  ) => {
    if (!rulesData || rulesData.length === 0) return incoterms;
    const existingCombinations = new Set();
    rulesData.forEach((rule: any) => {
      if (
        rule.transport_type === transportTypeId &&
        rule.shipment_type === bookingTypeId
      ) {
        existingCombinations.add(
          `${rule.transport_type}-${rule.shipment_type}-${rule.incoterm}`
        );
      }
    });

    return incoterms.filter(
      (inc) =>
        !existingCombinations.has(
          `${transportTypeId}-${bookingTypeId}-${inc.id}`
        )
    );
  };

  const getAvailableOriginPorts = (
    transportTypeId: number | null,
    bookingTypeId: number | null,
    incotermId: number | null
  ) => {
    if (!rulesData || rulesData.length === 0) return originPorts;
    const existingCombinations = new Set();
    rulesData.forEach((rule: any) => {
      if (
        rule.transport_type === transportTypeId &&
        rule.shipment_type === bookingTypeId &&
        rule.incoterm === incotermId
      ) {
        existingCombinations.add(
          `${rule.transport_type}-${rule.shipment_type}-${rule.incoterm}-${rule.origin_port}`
        );
      }
    });

    return originPorts.filter(
      (port) =>
        !existingCombinations.has(
          `${transportTypeId}-${bookingTypeId}-${incotermId}-${port.id}`
        )
    );
  };

  const getAvailableDestinationPorts = (
    transportTypeId: number | null,
    bookingTypeId: number | null,
    incotermId: number | null,
    originPortId: number | null
  ) => {
    if (!rulesData || rulesData.length === 0) return destinationPorts;
    const existingCombinations = new Set();
    rulesData.forEach((rule: any) => {
      if (
        rule.transport_type === transportTypeId &&
        rule.shipment_type === bookingTypeId &&
        rule.incoterm === incotermId &&
        rule.origin_port === originPortId
      ) {
        existingCombinations.add(
          `${rule.transport_type}-${rule.shipment_type}-${rule.incoterm}-${rule.origin_port}-${rule.destination_port}`
        );
      }
    });

    return destinationPorts.filter(
      (port) =>
        !existingCombinations.has(
          `${transportTypeId}-${bookingTypeId}-${incotermId}-${originPortId}-${port.id}`
        )
    );
  };

  // Load real pricing data from database
  const loadPricingData = async () => {
    try {
      setLoading(true);

      // Fetch pricing rules with related data
      const { data: rulesData, error: rulesError } = await supabase
        .from("pricing_rules")
        .select(
          `
          id, name, transport_type, origin_port, destination_port, 
          shipment_type, incoterm, is_active, valid_from, valid_to,
          transport_type_info:booking_transport_types(name),
          origin_port_info:booking_origin_port(name),
          destination_port_info:booking_destination_port(name)
        `
        )
        .eq("is_active", true);

      if (rulesError) throw rulesError;

      // Store rules data in state for availability checking
      setRulesData(rulesData || []);

      // Fetch pricing components for each rule
      const { data: componentsData, error: componentsError } = await supabase
        .from("pricing_values")
        .select(
          `
          pricing_rule_id, value, currency,
          pricing_component:pricing_components(
            id, name, component_type, calculation_type, behavior_type
          )
        `
        )
        .in("pricing_rule_id", rulesData?.map((r) => r.id) || []);

      if (componentsError) throw componentsError;

      // Transform data into tree structure
      const treeStructure = buildTreeFromRules(
        rulesData || [],
        componentsData || []
      );
      setTreeData(treeStructure);
      // Just expand the top-level transport types header
      setExpandedKeys(["header-transport"]);
    } catch (error) {
      console.error("Error loading pricing data:", error);
      message.error("Failed to load pricing data");

      // Fallback to empty data on error
      setTreeData([]);
      setExpandedKeys([]);
    } finally {
      setLoading(false);
    }
  };

  // Build hierarchical tree structure from flat rules data
  const buildTreeFromRules = (
    rules: any[],
    components: any[]
  ): PricingNode[] => {
    // Build the hierarchy directly without wrapping in "All Pricing Rules"
    return buildHierarchy(rules, components);
  };

  // Build the hierarchical structure step by step with TRUE INHERITANCE
  const buildHierarchy = (rules: any[], components: any[]): PricingNode[] => {
    // Add section header for Transport Types
    const transportTypeHeader: PricingNode = {
      id: "header-transport",
      key: "header-transport",
      nodeType: "folder",
      title: (
        <div
          style={{
            padding: "8px 12px",
            backgroundColor: "#e6f7ff",
            border: "1px solid #91d5ff",
            borderRadius: "6px",
            fontWeight: "bold",
            color: "#0050b3",
            cursor: "pointer",
            userSelect: "none",
          }}
        >
          📦 Transport Types
        </div>
      ),
      criteria: {},
      components: [],
      inheritedTotal: 0,
      ownTotal: 0,
      finalTotal: 0,
      children: buildTransportTypeLevel(rules, components, 0), // Pass inherited total
      selectable: false, // Make header non-selectable
    };

    return [transportTypeHeader];
  };

  // Build transport type level - NEW: WITH INHERITANCE AND SPECIAL "ANY" SECTION
  const buildTransportTypeLevel = (
    rules: any[],
    components: any[],
    inheritedFromParent: number
  ): PricingNode[] => {
    // Group by transport type first
    const transportGroups = groupBy(rules, (rule) => {
      const transportInfo = Array.isArray(rule.transport_type_info)
        ? rule.transport_type_info[0]
        : rule.transport_type_info;
      return transportInfo?.name || "Any Transport Type";
    });

    const result: PricingNode[] = [];

    // SPECIAL HANDLING: Put "Any Transport Type" first as a special root section
    if (transportGroups["Any Transport Type"]) {
      const anyTransportRules = transportGroups["Any Transport Type"];
      const anyTransportComponents = getComponentsForRules(
        anyTransportRules.map((r) => r.id),
        components
      );
      const ownTotal = calculateTotal(anyTransportComponents);
      const finalTotal = inheritedFromParent + ownTotal;

      result.push({
        id: "any-transport-type",
        key: "any-transport-type",
        nodeType: "folder" as const,
        title: renderNodeTitle(
          "🌍 Any Transport Type (Global Costs)",
          "folder",
          inheritedFromParent,
          ownTotal,
          finalTotal,
          "transport-header"
        ),
        criteria: {
          transportType: {
            id: 0,
            name: "Any Transport Type",
          },
        },
        components: anyTransportComponents,
        inheritedTotal: inheritedFromParent,
        ownTotal: ownTotal,
        finalTotal: finalTotal,
        children: [], // Any Transport Type doesn't have children in this structure
      });

      // Update inherited total for specific transport types
      inheritedFromParent = finalTotal;

      // Remove "Any Transport Type" from the groups so it doesn't appear again
      delete transportGroups["Any Transport Type"];
    }

    // Add a separator header for specific transport types
    if (Object.keys(transportGroups).length > 0) {
      result.push({
        id: "header-specific-transport",
        key: "header-specific-transport",
        nodeType: "folder",
        title: (
          <div
            style={{
              padding: "6px 10px",
              backgroundColor: "#f0f8ff",
              border: "1px solid #87ceeb",
              borderRadius: "6px",
              fontWeight: "bold",
              color: "#4682b4",
              fontSize: "13px",
              cursor: "pointer",
              userSelect: "none",
            }}
          >
            🚛 Specific Transport Types
          </div>
        ),
        criteria: {},
        components: [],
        inheritedTotal: 0,
        ownTotal: 0,
        finalTotal: 0,
        children: buildSpecificTransportTypes(
          transportGroups,
          components,
          inheritedFromParent
        ),
        selectable: false,
      });
    }

    return result;
  };

  // Build specific transport types (Airfreight, Seafreight, etc.)
  const buildSpecificTransportTypes = (
    transportGroups: Record<string, any[]>,
    components: any[],
    inheritedFromParent: number
  ): PricingNode[] => {
    // Sort transport types to put Airfreight and Seafreight first
    const sortedTransportTypes = Object.entries(transportGroups).sort(
      ([nameA], [nameB]) => {
        const orderMap: Record<string, number> = {
          Airfreight: 1,
          Seafreight: 2,
        };
        return (orderMap[nameA] || 999) - (orderMap[nameB] || 999);
      }
    );

    return sortedTransportTypes.map(([transportName, transportRules]) => {
      const transportKey = `transport-${transportName
        .toLowerCase()
        .replace(/\s+/g, "-")}`;

      // Get components for this transport type level
      const transportComponents = getComponentsForRules(
        transportRules.map((r) => r.id),
        components
      );
      const ownTotal = calculateTotal(transportComponents);
      const finalTotal = inheritedFromParent + ownTotal;

      // Add emoji icons for specific transport types
      const getTransportIcon = (name: string) => {
        if (name.toLowerCase().includes("air")) return "✈️";
        if (name.toLowerCase().includes("sea")) return "🚢";
        return "🚛";
      };

      return {
        id: transportKey,
        key: transportKey,
        nodeType: "folder" as const,
        title: renderNodeTitle(
          `${getTransportIcon(transportName)} ${transportName}`,
          "folder",
          inheritedFromParent,
          ownTotal,
          finalTotal,
          "transport-type",
          {
            transportType: {
              id: transportRules[0]?.transport_type || 0,
              name: transportName,
            },
          }
        ),
        criteria: {
          transportType: {
            id: transportRules[0]?.transport_type || 0,
            name: transportName,
          },
        },
        components: transportComponents,
        inheritedTotal: inheritedFromParent,
        ownTotal: ownTotal,
        finalTotal: finalTotal,
        children:
          transportName.toLowerCase() === "airfreight"
            ? buildIncotermLevel(
                transportRules,
                components,
                transportName,
                "All Booking Types", // Default booking type for airfreight
                finalTotal
              )
            : buildBookingTypeLevel(
                transportRules,
                components,
                transportName,
                finalTotal // Pass the accumulated total to children
              ),
      };
    });
  };

  // Level 2: Booking Type - NEW: WITH INHERITANCE
  const buildBookingTypeLevel = (
    rules: any[],
    components: any[],
    transportName: string,
    inheritedFromParent: number
  ): PricingNode[] => {
    // Add section header for Booking Types
    const bookingTypeHeader: PricingNode = {
      id: `header-booking-${transportName}`,
      key: `header-booking-${transportName}`,
      nodeType: "folder",
      title: (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "6px 10px",
            backgroundColor: "#f6ffed",
            border: "1px solid #b7eb8f",
            borderRadius: "6px",
            fontWeight: "bold",
            color: "#389e0d",
            fontSize: "13px",
            cursor: "pointer",
            userSelect: "none",
          }}
        >
          <span>📋 Booking Types</span>
          <Button
            type="primary"
            size="small"
            icon={<PlusOutlined />}
            style={{
              fontSize: "11px",
              height: "24px",
              padding: "0 8px",
            }}
            onClick={(e) => {
              e.stopPropagation();
              setSelectionModalType("booking");
              setSelectionModalParent({
                transportType: {
                  id: rules[0]?.transport_type || 0,
                  name: transportName,
                },
              });
              setSelectionModalVisible(true);
            }}
          >
            Add Booking Type
          </Button>
        </div>
      ),
      criteria: {},
      components: [],
      inheritedTotal: 0,
      ownTotal: 0,
      finalTotal: 0,
      children: buildBookingTypeNodes(
        rules,
        components,
        transportName,
        inheritedFromParent
      ),
      selectable: false,
    };

    return [bookingTypeHeader];
  };

  // Build actual booking type nodes - NEW: WITH INHERITANCE
  const buildBookingTypeNodes = (
    rules: any[],
    components: any[],
    transportName: string,
    inheritedFromParent: number
  ): PricingNode[] => {
    const bookingGroups = groupBy(
      rules,
      (rule) => rule.booking_type_info?.name || "Other Booking Types"
    );

    return Object.entries(bookingGroups).map(([bookingName, bookingRules]) => {
      const bookingKey = `${transportName}-${bookingName}`
        .toLowerCase()
        .replace(/\s+/g, "-");

      // Get components for this booking type level
      const bookingComponents = getComponentsForRules(
        bookingRules.map((r) => r.id),
        components
      );
      const ownTotal = calculateTotal(bookingComponents);
      const finalTotal = inheritedFromParent + ownTotal;

      return {
        id: bookingKey,
        key: bookingKey,
        nodeType: "folder" as const,
        title: renderNodeTitle(
          bookingName,
          "folder",
          inheritedFromParent,
          ownTotal,
          finalTotal,
          "booking-type",
          {
            transportType: {
              id: bookingRules[0]?.transport_type || 0,
              name: transportName,
            },
          }
        ),
        criteria: {
          transportType: {
            id: bookingRules[0]?.transport_type || 0,
            name: transportName,
          },
          bookingType: {
            id: bookingRules[0]?.booking_type || 0,
            name: bookingName,
          },
        },
        components: bookingComponents,
        inheritedTotal: inheritedFromParent,
        ownTotal: ownTotal,
        finalTotal: finalTotal,
        children: buildIncotermLevel(
          bookingRules,
          components,
          transportName,
          bookingName,
          finalTotal // Pass the accumulated total to children
        ),
      };
    });
  };

  // Level 3: Incoterm - NEW: WITH INHERITANCE
  const buildIncotermLevel = (
    rules: any[],
    components: any[],
    transportName: string,
    shipmentName: string,
    inheritedFromParent: number
  ): PricingNode[] => {
    // Add section header for Incoterms
    const incotermHeader: PricingNode = {
      id: `header-incoterm-${transportName}-${shipmentName}`,
      key: `header-incoterm-${transportName}-${shipmentName}`,
      nodeType: "folder",
      title: (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "6px 10px",
            backgroundColor: "#fff7e6",
            border: "1px solid #ffd666",
            borderRadius: "6px",
            fontWeight: "bold",
            color: "#ad6800",
            fontSize: "13px",
            cursor: "pointer",
            userSelect: "none",
          }}
        >
          <span>📄 Incoterms</span>
          <Button
            type="primary"
            size="small"
            icon={<PlusOutlined />}
            style={{
              fontSize: "11px",
              height: "24px",
              padding: "0 8px",
            }}
            onClick={(e) => {
              e.stopPropagation();
              setSelectionModalType("incoterm");
              setSelectionModalParent({
                transportType: {
                  id: rules[0]?.transport_type || 0,
                  name: transportName,
                },
                bookingType: {
                  id: rules[0]?.shipment_type || 0,
                  name: shipmentName,
                },
              });
              setSelectionModalVisible(true);
            }}
          >
            Add Incoterm
          </Button>
        </div>
      ),
      criteria: {},
      components: [],
      inheritedTotal: 0,
      ownTotal: 0,
      finalTotal: 0,
      children: buildIncotermNodes(
        rules,
        components,
        transportName,
        shipmentName,
        inheritedFromParent
      ),
      selectable: false,
    };

    return [incotermHeader];
  };

  // Build actual incoterm nodes - NEW: WITH INHERITANCE
  const buildIncotermNodes = (
    rules: any[],
    components: any[],
    transportName: string,
    shipmentName: string,
    inheritedFromParent: number
  ): PricingNode[] => {
    const incotermGroups = groupBy(
      rules,
      (rule) => rule.incoterm_info?.name || "Any Incoterm"
    );

    return Object.entries(incotermGroups).map(
      ([incotermName, incotermRules]) => {
        const incotermKey = `${transportName}-${shipmentName}-${incotermName}`
          .toLowerCase()
          .replace(/\s+/g, "-");

        // Get components for this incoterm level
        const incotermComponents = getComponentsForRules(
          incotermRules.map((r) => r.id),
          components
        );
        const ownTotal = calculateTotal(incotermComponents);
        const finalTotal = inheritedFromParent + ownTotal;

        const nextLevel =
          transportName.toLowerCase() === "seafreight"
            ? buildContainerSizeLevel(
                incotermRules,
                components,
                transportName,
                shipmentName,
                incotermName,
                finalTotal // Pass the accumulated total to children
              )
            : buildOriginPortLevel(
                incotermRules,
                components,
                transportName,
                shipmentName,
                incotermName,
                undefined,
                finalTotal // Pass the accumulated total to children - skip container sizes for airfreight
              );

        return {
          id: incotermKey,
          key: incotermKey,
          nodeType: "folder" as const,
          title: renderNodeTitle(
            incotermName,
            "folder",
            inheritedFromParent,
            ownTotal,
            finalTotal,
            "incoterm",
            {
              transportType: {
                id: incotermRules[0]?.transport_type || 0,
                name: transportName,
              },
              bookingType: {
                id: incotermRules[0]?.shipment_type || 0,
                name: shipmentName,
              },
            }
          ),
          criteria: {
            transportType: {
              id: incotermRules[0]?.transport_type || 0,
              name: transportName,
            },
            bookingType: {
              id: incotermRules[0]?.shipment_type || 0,
              name: shipmentName,
            },
            incoterm: {
              id: incotermRules[0]?.incoterm || 0,
              name: incotermName,
            },
          },
          components: incotermComponents,
          inheritedTotal: inheritedFromParent,
          ownTotal: ownTotal,
          finalTotal: finalTotal,
          children: nextLevel,
        };
      }
    );
  };

  // Level 4: Container Size (only for Seafreight) - NEW: WITH INHERITANCE
  const buildContainerSizeLevel = (
    rules: any[],
    components: any[],
    transportName: string,
    shipmentName: string,
    incotermName: string,
    inheritedFromParent: number
  ): PricingNode[] => {
    // Add section header for Container Sizes
    const containerSizeHeader: PricingNode = {
      id: `header-container-${transportName}-${shipmentName}-${incotermName}`,
      key: `header-container-${transportName}-${shipmentName}-${incotermName}`,
      nodeType: "folder",
      title: (
        <div
          style={{
            padding: "6px 10px",
            backgroundColor: "#f0f5ff",
            border: "1px solid #91caff",
            borderRadius: "6px",
            fontWeight: "bold",
            color: "#003a8c",
            fontSize: "13px",
            cursor: "pointer",
            userSelect: "none",
          }}
        >
          📦 Container Sizes
        </div>
      ),
      criteria: {},
      components: [],
      inheritedTotal: 0,
      ownTotal: 0,
      finalTotal: 0,
      children: buildContainerSizeNodes(
        rules,
        components,
        transportName,
        shipmentName,
        incotermName,
        inheritedFromParent
      ),
      selectable: false,
    };

    return [containerSizeHeader];
  };

  // Build actual container size nodes - NEW: WITH INHERITANCE
  const buildContainerSizeNodes = (
    rules: any[],
    components: any[],
    transportName: string,
    shipmentName: string,
    incotermName: string,
    inheritedFromParent: number
  ): PricingNode[] => {
    const containerGroups = groupBy(
      rules,
      (rule) => rule.container_size || "Any Container Size"
    );

    return Object.entries(containerGroups).map(
      ([containerSize, containerRules]) => {
        const containerKey =
          `${transportName}-${shipmentName}-${incotermName}-${containerSize}`
            .toLowerCase()
            .replace(/\s+/g, "-");

        // Get components for this container size level
        const containerComponents = getComponentsForRules(
          containerRules.map((r) => r.id),
          components
        );
        const ownTotal = calculateTotal(containerComponents);
        const finalTotal = inheritedFromParent + ownTotal;

        return {
          id: containerKey,
          key: containerKey,
          nodeType: "folder" as const,
          title: renderNodeTitle(
            containerSize === "Any Container Size"
              ? containerSize
              : `${containerSize} Container`,
            "folder",
            inheritedFromParent,
            ownTotal,
            finalTotal
          ),
          criteria: {
            transportType: {
              id: containerRules[0]?.transport_type || 0,
              name: transportName,
            },
            shipmentType: {
              id: containerRules[0]?.shipment_type || 0,
              name: shipmentName,
            },
            incoterm: {
              id: containerRules[0]?.incoterm || 0,
              name: incotermName,
            },
            containerSize: { id: 0, name: containerSize },
          },
          components: containerComponents,
          inheritedTotal: inheritedFromParent,
          ownTotal: ownTotal,
          finalTotal: finalTotal,
          children: buildOriginPortLevel(
            containerRules,
            components,
            transportName,
            shipmentName,
            incotermName,
            containerSize,
            finalTotal // Pass the accumulated total to children
          ),
        };
      }
    );
  };

  // Level 5: Origin Port - NEW: WITH INHERITANCE
  const buildOriginPortLevel = (
    rules: any[],
    components: any[],
    transportName: string,
    shipmentName: string,
    incotermName: string,
    containerSize: string | undefined,
    inheritedFromParent: number
  ): PricingNode[] => {
    // Add section header for Origin Ports
    const originPortHeader: PricingNode = {
      id: `header-origin-${transportName}-${shipmentName}-${incotermName}${
        containerSize ? `-${containerSize}` : ""
      }`,
      key: `header-origin-${transportName}-${shipmentName}-${incotermName}${
        containerSize ? `-${containerSize}` : ""
      }`,
      nodeType: "folder",
      title: (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "6px 10px",
            backgroundColor: "#fff2e8",
            border: "1px solid #ffb366",
            borderRadius: "6px",
            fontWeight: "bold",
            color: "#ad4e00",
            fontSize: "13px",
            cursor: "pointer",
            userSelect: "none",
          }}
        >
          <span>🛫 Origin Ports</span>
          <Button
            type="primary"
            size="small"
            icon={<PlusOutlined />}
            style={{
              fontSize: "11px",
              height: "24px",
              padding: "0 8px",
            }}
            onClick={(e) => {
              e.stopPropagation();
              setSelectionModalType("origin");
              setSelectionModalParent({
                transportType: {
                  id: rules[0]?.transport_type || 0,
                  name: transportName,
                },
                bookingType: {
                  id: rules[0]?.shipment_type || 0,
                  name: shipmentName,
                },
                incoterm: {
                  id: rules[0]?.incoterm || 0,
                  name: incotermName,
                },
                ...(containerSize && {
                  containerSize: { id: 0, name: containerSize },
                }),
              });
              setSelectionModalVisible(true);
            }}
          >
            Add Origin Port
          </Button>
        </div>
      ),
      criteria: {},
      components: [],
      inheritedTotal: 0,
      ownTotal: 0,
      finalTotal: 0,
      children: buildOriginPortNodes(
        rules,
        components,
        transportName,
        shipmentName,
        incotermName,
        containerSize,
        inheritedFromParent
      ),
      selectable: false,
    };

    return [originPortHeader];
  };

  // Build actual origin port nodes - NEW: WITH INHERITANCE
  const buildOriginPortNodes = (
    rules: any[],
    components: any[],
    transportName: string,
    shipmentName: string,
    incotermName: string,
    containerSize: string | undefined,
    inheritedFromParent: number
  ): PricingNode[] => {
    const originGroups = groupBy(rules, (rule) => {
      const originInfo = Array.isArray(rule.origin_port_info)
        ? rule.origin_port_info[0]
        : rule.origin_port_info;
      return originInfo?.name || "Any Origin Port";
    });

    return Object.entries(originGroups).map(([originName, originRules]) => {
      const originKey = `${transportName}-${shipmentName}-${incotermName}${
        containerSize ? `-${containerSize}` : ""
      }-${originName}`
        .toLowerCase()
        .replace(/\s+/g, "-");

      // Get components for this origin port level
      const originComponents = getComponentsForRules(
        originRules.map((r) => r.id),
        components
      );
      const ownTotal = calculateTotal(originComponents);
      const finalTotal = inheritedFromParent + ownTotal;

      return {
        id: originKey,
        key: originKey,
        nodeType: "folder" as const,
        title: renderNodeTitle(
          originName === "Any Origin Port" ? originName : `From ${originName}`,
          "folder",
          inheritedFromParent,
          ownTotal,
          finalTotal,
          "origin-port",
          {
            transportType: {
              id: originRules[0]?.transport_type || 0,
              name: transportName,
            },
            bookingType: {
              id: originRules[0]?.shipment_type || 0,
              name: shipmentName,
            },
            incoterm: { id: originRules[0]?.incoterm || 0, name: incotermName },
            ...(containerSize && {
              containerSize: { id: 0, name: containerSize },
            }),
          }
        ),
        criteria: {
          transportType: {
            id: originRules[0]?.transport_type || 0,
            name: transportName,
          },
          shipmentType: {
            id: originRules[0]?.shipment_type || 0,
            name: shipmentName,
          },
          incoterm: { id: originRules[0]?.incoterm || 0, name: incotermName },
          ...(containerSize && {
            containerSize: { id: 0, name: containerSize },
          }),
          originPort: {
            id: originRules[0]?.origin_port || 0,
            name: originName,
          },
        },
        components: originComponents,
        inheritedTotal: inheritedFromParent,
        ownTotal: ownTotal,
        finalTotal: finalTotal,
        children: buildDestinationPortLevel(
          originRules,
          components,
          finalTotal
        ),
      };
    });
  };

  // Level 6: Destination Port (final level) - NEW: WITH INHERITANCE
  const buildDestinationPortLevel = (
    rules: any[],
    components: any[],
    inheritedFromParent: number
  ): PricingNode[] => {
    // Add section header for Destination Ports
    const destinationPortHeader: PricingNode = {
      id: `header-destination-${Date.now()}`,
      key: `header-destination-${Date.now()}`,
      nodeType: "folder",
      title: (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "6px 10px",
            backgroundColor: "#f9f0ff",
            border: "1px solid #d3adf7",
            borderRadius: "6px",
            fontWeight: "bold",
            color: "#531dab",
            fontSize: "13px",
            cursor: "pointer",
            userSelect: "none",
          }}
        >
          <span>🛬 Destination Ports</span>
          <Button
            type="primary"
            size="small"
            icon={<PlusOutlined />}
            style={{
              fontSize: "11px",
              height: "24px",
              padding: "0 8px",
            }}
            onClick={(e) => {
              e.stopPropagation();
              setSelectionModalType("destination");
              setSelectionModalParent({
                transportType: {
                  id: rules[0]?.transport_type || 0,
                  name: rules[0]?.transport_type_info?.name || "Unknown",
                },
                bookingType: {
                  id: rules[0]?.shipment_type || 0,
                  name: rules[0]?.shipment_type_info?.name || "Unknown",
                },
                incoterm: {
                  id: rules[0]?.incoterm || 0,
                  name: rules[0]?.incoterm_info?.name || "Unknown",
                },
                originPort: {
                  id: rules[0]?.origin_port || 0,
                  name: rules[0]?.origin_port_info?.name || "Unknown",
                },
              });
              setSelectionModalVisible(true);
            }}
          >
            Add Destination Port
          </Button>
        </div>
      ),
      criteria: {},
      components: [],
      inheritedTotal: 0,
      ownTotal: 0,
      finalTotal: 0,
      children: buildDestinationPortNodes(
        rules,
        components,
        inheritedFromParent
      ),
      selectable: false,
    };

    return [destinationPortHeader];
  };

  // Build actual destination port nodes - NEW: WITH INHERITANCE
  const buildDestinationPortNodes = (
    rules: any[],
    components: any[],
    inheritedFromParent: number
  ): PricingNode[] => {
    const destinationGroups = groupBy(rules, (rule) => {
      const destInfo = Array.isArray(rule.destination_port_info)
        ? rule.destination_port_info[0]
        : rule.destination_port_info;
      return destInfo?.name || "Any Destination Port";
    });

    return Object.entries(destinationGroups).map(
      ([destinationName, destRules]) => {
        // Get components for this destination port level
        const ruleComponents = getComponentsForRules(
          destRules.map((r) => r.id),
          components
        );
        const ownTotal = calculateTotal(ruleComponents);
        const finalTotal = inheritedFromParent + ownTotal;

        // Use the first rule's data for the leaf node
        const rule = destRules[0];

        return {
          id: `rule-${rule.id}`,
          key: `rule-${rule.id}`,
          nodeType: "specific" as const,
          title: renderNodeTitle(
            destinationName === "Any Destination Port"
              ? destinationName
              : `To ${destinationName}`,
            "specific",
            inheritedFromParent,
            ownTotal,
            finalTotal,
            "destination-port",
            {
              transportType: rule.transport_type
                ? {
                    id: rule.transport_type,
                    name: rule.transport_type_info?.name || "Unknown",
                  }
                : undefined,
              bookingType: rule.shipment_type
                ? {
                    id: rule.shipment_type,
                    name: rule.shipment_type_info?.name || "Unknown",
                  }
                : undefined,
              incoterm: rule.incoterm
                ? {
                    id: rule.incoterm,
                    name: rule.incoterm_info?.name || "Unknown",
                  }
                : undefined,
              originPort: rule.origin_port_info
                ? {
                    id: rule.origin_port,
                    name: Array.isArray(rule.origin_port_info)
                      ? rule.origin_port_info[0]?.name
                      : rule.origin_port_info.name,
                  }
                : undefined,
            }
          ),
          criteria: {
            transportType: rule.transport_type
              ? {
                  id: rule.transport_type,
                  name: rule.transport_type_info?.name || "Unknown",
                }
              : undefined,
            shipmentType: rule.shipment_type
              ? {
                  id: rule.shipment_type,
                  name: rule.shipment_type_info?.name || "Unknown",
                }
              : undefined,
            incoterm: rule.incoterm
              ? {
                  id: rule.incoterm,
                  name: rule.incoterm_info?.name || "Unknown",
                }
              : undefined,
            originPort: rule.origin_port_info
              ? {
                  id: rule.origin_port,
                  name: Array.isArray(rule.origin_port_info)
                    ? rule.origin_port_info[0]?.name
                    : rule.origin_port_info.name,
                }
              : undefined,
            destinationPort: rule.destination_port_info
              ? {
                  id: rule.destination_port,
                  name: Array.isArray(rule.destination_port_info)
                    ? rule.destination_port_info[0]?.name
                    : rule.destination_port_info.name,
                }
              : undefined,
          },
          components: ruleComponents,
          inheritedTotal: inheritedFromParent,
          ownTotal: ownTotal,
          finalTotal: finalTotal,
          isLeaf: true,
        };
      }
    );
  };

  // Get components for specific rules
  const getComponentsForRules = (ruleIds: number[], allComponents: any[]) => {
    return allComponents
      .filter((comp) => ruleIds.includes(comp.pricing_rule_id))
      .map((comp) => ({
        id: comp.pricing_component.id.toString(),
        name: comp.pricing_component.name,
        value: parseFloat(comp.value),
        currency: comp.currency,
        appliesEverywhere: false, // These are rule-specific components, not global inherited ones
        componentType: comp.pricing_component.component_type,
        calculationType: comp.pricing_component.calculation_type,
      }));
  };

  // Calculate total value of components
  const calculateTotal = (components: any[]) => {
    return components.reduce((total, comp) => total + comp.value, 0);
  };

  useEffect(() => {
    loadPricingData();
    loadPortsAndTransportTypes();
  }, []);

  const loadPortsAndTransportTypes = async () => {
    try {
      // Load origin ports
      const { data: originData } = await supabase
        .from("booking_origin_port")
        .select("*")
        .order("name");
      setOriginPorts(originData || []);

      // Load destination ports
      const { data: destData } = await supabase
        .from("booking_destination_port")
        .select("*")
        .order("name");
      setDestinationPorts(destData || []);

      // Load transport types
      const { data: transportData } = await supabase
        .from("booking_transport_types")
        .select("*")
        .order("name");
      setTransportTypes(transportData || []);

      // Load incoterms from database
      const { data: incotermsData } = await supabase
        .from("bookings_incoterm")
        .select("*")
        .order("name");
      setIncoterms(incotermsData || []);

      // Load pricing components
      const { data: componentsData } = await supabase
        .from("pricing_components")
        .select("*")
        .order("name");
      setAvailableComponents(componentsData || []);
    } catch (error) {
      console.error(
        "Error loading ports, transport types, and components:",
        error
      );
    }
  };

  const handleAddRootRule = () => {
    setSelectionModalType("transport");
    setSelectionModalParent(null); // No parent for root level
    setSelectionModalVisible(true);
  };

  const handleQuickAction = (action: string) => {
    if (!selectedNodeData && action !== "add-transport") {
      message.warning("Please select a rule first");
      return;
    }

    // Get current criteria to use as context for new rule
    const currentCriteria = selectedNodeData?.criteria || {};

    switch (action) {
      case "add-transport":
        setSelectionModalType("transport");
        setSelectionModalParent(null);
        setSelectionModalVisible(true);
        message.info("Adding a new Transport Type");
        break;

      case "add-shipment":
        setSelectionModalType("booking");
        setSelectionModalParent(currentCriteria);
        setSelectionModalVisible(true);
        message.info(
          `Adding Shipment Type under ${currentCriteria.transportType?.name}`
        );
        break;

      case "add-incoterm":
        setSelectionModalType("incoterm");
        setSelectionModalParent(currentCriteria);
        setSelectionModalVisible(true);
        message.info(
          `Adding Incoterm under ${currentCriteria.transportType?.name} → ${currentCriteria.bookingType?.name}`
        );
        break;

      case "add-origin":
        setSelectionModalType("origin");
        setSelectionModalParent(currentCriteria);
        setSelectionModalVisible(true);
        message.info("Adding Origin Port to current context");
        break;

      case "add-destination":
        setSelectionModalType("destination");
        setSelectionModalParent(currentCriteria);
        setSelectionModalVisible(true);
        message.info("Adding Destination Port to current context");
        break;

      default:
        message.error("Unknown action");
    }
  };

  const handleComponentToggle = (componentId: string, checked: boolean) => {
    const newSelectedComponents = new Set(selectedComponents);
    if (checked) {
      newSelectedComponents.add(componentId);
    } else {
      newSelectedComponents.delete(componentId);
    }
    setSelectedComponents(newSelectedComponents);
  };

  const resetComponentSelection = () => {
    setSelectedComponents(new Set());
    form.resetFields();
  };

  // Helper function to generate rule name from criteria
  const generateRuleName = (criteria: any): string => {
    const parts: string[] = [];

    if (criteria.transportType) {
      parts.push(criteria.transportType.name);
    }
    if (criteria.bookingType) {
      parts.push(criteria.bookingType.name);
    }
    if (criteria.incoterm) {
      parts.push(criteria.incoterm.name);
    }
    if (criteria.originPort) {
      parts.push(`From ${criteria.originPort.name}`);
    }
    if (criteria.destinationPort) {
      parts.push(`To ${criteria.destinationPort.name}`);
    }

    return parts.length > 0 ? parts.join(" - ") : "General Rule";
  };

  const handleComponentToggleInline = async (
    componentId: string,
    checked: boolean,
    node: PricingNode
  ) => {
    try {
      setLoading(true);

      // Get or create rule ID
      let ruleId: number;
      let wasRuleCreated = false;

      if (node.id.startsWith("rule-")) {
        // This is already a database rule
        ruleId = parseInt(node.id.replace("rule-", ""));
      } else {
        // This is a folder node - we need to find or create a database rule
        if (!checked) {
          // User is trying to remove a component from a folder node
          // Let's try to find the existing rule that matches this configuration
          let query = supabase
            .from("pricing_rules")
            .select("id")
            .eq("is_active", true);

          // Add conditions using proper null handling
          if (node.criteria.transportType?.id) {
            query = query.eq("transport_type", node.criteria.transportType.id);
          } else {
            query = query.is("transport_type", null);
          }

          if (node.criteria.bookingType?.id) {
            query = query.eq("shipment_type", node.criteria.bookingType.id);
          } else {
            query = query.is("shipment_type", null);
          }

          if (node.criteria.incoterm?.id) {
            query = query.eq("incoterm", node.criteria.incoterm.id);
          } else {
            query = query.is("incoterm", null);
          }

          if (node.criteria.originPort?.id) {
            query = query.eq("origin_port", node.criteria.originPort.id);
          } else {
            query = query.is("origin_port", null);
          }

          if (node.criteria.destinationPort?.id) {
            query = query.eq(
              "destination_port",
              node.criteria.destinationPort.id
            );
          } else {
            query = query.is("destination_port", null);
          }

          const { data: existingRules, error: searchError } = await query;

          if (searchError) throw searchError;

          if (existingRules && existingRules.length > 0) {
            ruleId = existingRules[0].id;
          } else {
            message.warning(
              "Cannot remove component - no rule exists for this configuration"
            );
            return;
          }
        } else {
          // We need to create a database rule for adding a component
          const ruleName = generateRuleName(node.criteria);

          const insertData: any = {
            name: ruleName,
            transport_type: node.criteria.transportType?.id || null,
            shipment_type: node.criteria.bookingType?.id || null,
            incoterm: node.criteria.incoterm?.id || null,
            origin_port: node.criteria.originPort?.id || null,
            destination_port: node.criteria.destinationPort?.id || null,
            valid_from: new Date().toISOString(),
            is_active: true,
          };

          const { data: ruleData, error: ruleError } = await supabase
            .from("pricing_rules")
            .insert(insertData)
            .select()
            .single();

          if (ruleError) throw ruleError;

          ruleId = ruleData.id;
          wasRuleCreated = true;

          // For new rules, we'll need a full reload to update tree structure
          const currentExpandedKeys = [...expandedKeys];
          const currentCriteria = node.criteria;

          await loadPricingData();
          setExpandedKeys(currentExpandedKeys);

          // Find and select the newly created rule
          setTimeout(() => {
            const findNewRule = (nodes: PricingNode[]): PricingNode | null => {
              for (const treeNode of nodes) {
                if (treeNode.id === `rule-${ruleId}`) {
                  return treeNode;
                }
                if (treeNode.children) {
                  const found = findNewRule(treeNode.children);
                  if (found) return found;
                }
              }
              return null;
            };

            const newRuleNode = findNewRule(treeData);
            if (newRuleNode) {
              setSelectedKeys([newRuleNode.key]);
              setSelectedNodeData(newRuleNode);
            }
          }, 100);
        }
      }

      if (checked) {
        // Add component to the rule
        const component = availableComponents.find(
          (c) => c.id.toString() === componentId
        );
        if (!component) return;

        const { error } = await supabase.from("pricing_values").insert({
          pricing_rule_id: ruleId,
          pricing_component_id: parseInt(componentId),
          value: 0, // Use 0 as default, user can change it later
          currency: "EUR",
        });

        if (error) throw error;

        // IMMEDIATELY update UI state for existing rules without full reload
        if (node.id.startsWith("rule-")) {
          setSelectedNodeData((prev) => {
            if (!prev) return prev;

            const newComponent = {
              id: componentId,
              name: component.name,
              value: 0,
              currency: "EUR",
              appliesEverywhere: false,
              componentType: component.component_type,
              calculationType: component.calculation_type,
            };

            const updatedComponents = [...prev.components, newComponent];
            const newOwnTotal = updatedComponents.reduce(
              (sum, c) => sum + c.value,
              0
            );
            const newFinalTotal = prev.inheritedTotal + newOwnTotal;

            return {
              ...prev,
              components: updatedComponents,
              ownTotal: newOwnTotal,
              finalTotal: newFinalTotal,
            };
          });

          // Also update the tree data to reflect the change while preserving expanded state
          const currentExpandedKeys = [...expandedKeys];

          setTreeData((prevTreeData) => {
            const updateNodeInTree = (nodes: PricingNode[]): PricingNode[] => {
              return nodes.map((treeNode) => {
                if (treeNode.id === node.id) {
                  const newComponent = {
                    id: componentId,
                    name: component.name,
                    value: 0,
                    currency: "EUR",
                    appliesEverywhere: false,
                    componentType: component.component_type,
                    calculationType: component.calculation_type,
                  };
                  const updatedComponents = [
                    ...treeNode.components,
                    newComponent,
                  ];
                  const newOwnTotal = updatedComponents.reduce(
                    (sum, c) => sum + c.value,
                    0
                  );
                  const newFinalTotal = treeNode.inheritedTotal + newOwnTotal;

                  return {
                    ...treeNode,
                    components: updatedComponents,
                    ownTotal: newOwnTotal,
                    finalTotal: newFinalTotal,
                  };
                }
                if (treeNode.children) {
                  return {
                    ...treeNode,
                    children: updateNodeInTree(treeNode.children),
                  };
                }
                return treeNode;
              });
            };
            return updateNodeInTree(prevTreeData);
          });

          // Preserve expanded state by updating it in the same render cycle
          setExpandedKeys(currentExpandedKeys);
        }

        // Show appropriate message based on whether rule was created
        if (wasRuleCreated) {
          message.success(`Created rule and added ${component.name}`);
        } else {
          message.success(`Added ${component.name} to rule`);
        }
      } else {
        // Remove component from the rule
        const { error, data } = await supabase
          .from("pricing_values")
          .delete()
          .eq("pricing_rule_id", ruleId)
          .eq("pricing_component_id", parseInt(componentId));

        if (error) {
          console.error("Delete error:", error);
          throw error;
        }

        console.log("Delete successful, response:", data);

        // Small delay to ensure database operation is fully committed
        await new Promise((resolve) => setTimeout(resolve, 50));

        // Update UI state for both database rules and folder nodes
        setSelectedNodeData((prev) => {
          if (!prev) return prev;

          console.log("Before removal - components:", prev.components.length);
          const updatedComponents = prev.components.filter(
            (c) => c.id !== componentId
          );
          console.log("After removal - components:", updatedComponents.length);

          const newOwnTotal = updatedComponents.reduce(
            (sum, c) => sum + c.value,
            0
          );
          const newFinalTotal = prev.inheritedTotal + newOwnTotal;

          return {
            ...prev,
            components: updatedComponents,
            ownTotal: newOwnTotal,
            finalTotal: newFinalTotal,
          };
        });

        // Also update the tree data to reflect the change while preserving expanded state
        const currentExpandedKeys = [...expandedKeys];

        setTreeData((prevTreeData) => {
          const updateNodeInTree = (nodes: PricingNode[]): PricingNode[] => {
            return nodes.map((treeNode) => {
              if (treeNode.id === node.id) {
                const updatedComponents = treeNode.components.filter(
                  (c) => c.id !== componentId
                );
                const newOwnTotal = updatedComponents.reduce(
                  (sum, c) => sum + c.value,
                  0
                );
                const newFinalTotal = treeNode.inheritedTotal + newOwnTotal;

                return {
                  ...treeNode,
                  components: updatedComponents,
                  ownTotal: newOwnTotal,
                  finalTotal: newFinalTotal,
                };
              }
              if (treeNode.children) {
                return {
                  ...treeNode,
                  children: updateNodeInTree(treeNode.children),
                };
              }
              return treeNode;
            });
          };
          return updateNodeInTree(prevTreeData);
        });

        // Preserve expanded state by updating it in the same render cycle
        setExpandedKeys(currentExpandedKeys);

        const component = availableComponents.find(
          (c) => c.id.toString() === componentId
        );
        message.success(`Removed ${component?.name || "component"} from rule`);
      }
    } catch (error) {
      console.error("Error updating component:", error);
      message.error("Failed to update component");
    } finally {
      setLoading(false);
    }
  };

  const handleComponentValueChange = (
    componentId: string,
    value: number,
    node: PricingNode
  ) => {
    // Update local state immediately for responsive UI
    setSelectedNodeData((prev) => {
      if (!prev) return prev;
      const updatedComponents = prev.components.map((c) =>
        c.id === componentId ? { ...c, value } : c
      );
      const newOwnTotal = updatedComponents.reduce(
        (sum, c) => sum + c.value,
        0
      );
      const newFinalTotal = prev.inheritedTotal + newOwnTotal;

      return {
        ...prev,
        components: updatedComponents,
        ownTotal: newOwnTotal,
        finalTotal: newFinalTotal,
      };
    });

    // Debounce the database update
    const updateKey = `${node.id}-${componentId}`;

    // Clear existing timeout for this component
    const existingTimeout = updateTimeouts.get(updateKey);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    // Set new timeout for database update
    const newTimeout = setTimeout(async () => {
      try {
        // Get rule ID - same logic as handleComponentToggleInline
        let ruleId: number;

        if (node.id.startsWith("rule-")) {
          ruleId = parseInt(node.id.replace("rule-", ""));
        } else {
          // For folder nodes, find existing rule
          let query = supabase
            .from("pricing_rules")
            .select("id")
            .eq("is_active", true);

          // Add conditions using proper null handling
          if (node.criteria.transportType?.id) {
            query = query.eq("transport_type", node.criteria.transportType.id);
          } else {
            query = query.is("transport_type", null);
          }

          if (node.criteria.bookingType?.id) {
            query = query.eq("shipment_type", node.criteria.bookingType.id);
          } else {
            query = query.is("shipment_type", null);
          }

          if (node.criteria.incoterm?.id) {
            query = query.eq("incoterm", node.criteria.incoterm.id);
          } else {
            query = query.is("incoterm", null);
          }

          if (node.criteria.originPort?.id) {
            query = query.eq("origin_port", node.criteria.originPort.id);
          } else {
            query = query.is("origin_port", null);
          }

          if (node.criteria.destinationPort?.id) {
            query = query.eq(
              "destination_port",
              node.criteria.destinationPort.id
            );
          } else {
            query = query.is("destination_port", null);
          }

          const { data: existingRules, error: searchError } = await query;
          if (searchError) throw searchError;

          if (existingRules && existingRules.length > 0) {
            ruleId = existingRules[0].id;
          } else {
            console.error("No rule found for value update");
            return;
          }
        }

        console.log(`[DEBUG] Updating component value:`, {
          componentId,
          value,
          ruleId,
          parsedComponentId: parseInt(componentId),
        });

        const { error, data } = await supabase
          .from("pricing_values")
          .update({ value: value })
          .eq("pricing_rule_id", ruleId)
          .eq("pricing_component_id", parseInt(componentId))
          .select();

        console.log(`[DEBUG] Update result:`, { error, data });

        if (error) throw error;

        if (!data || data.length === 0) {
          console.error(
            "[DEBUG] No rows were updated. This pricing value might not exist."
          );
          message.warning(
            "Component value not found. Please refresh and try again."
          );
          return;
        }

        // Clean up timeout
        setUpdateTimeouts((prev) => {
          const newMap = new Map(prev);
          newMap.delete(updateKey);
          return newMap;
        });
      } catch (error) {
        console.error("Error updating component value:", error);
        message.error("Failed to update component value");
      }
    }, 800); // 800ms delay

    // Store the timeout
    setUpdateTimeouts((prev) => {
      const newMap = new Map(prev);
      newMap.set(updateKey, newTimeout);
      return newMap;
    });
  };

  const handleComponentCurrencyChange = async (
    componentId: string,
    currency: string,
    node: PricingNode
  ) => {
    try {
      // Get rule ID - same logic as other component functions
      let ruleId: number;

      if (node.id.startsWith("rule-")) {
        ruleId = parseInt(node.id.replace("rule-", ""));
      } else {
        // For folder nodes, find the existing rule based on criteria
        let query = supabase
          .from("pricing_rules")
          .select("id")
          .eq("is_active", true);

        // Add conditions using proper null handling
        if (node.criteria.transportType?.id) {
          query = query.eq("transport_type", node.criteria.transportType.id);
        } else {
          query = query.is("transport_type", null);
        }

        if (node.criteria.bookingType?.id) {
          query = query.eq("shipment_type", node.criteria.bookingType.id);
        } else {
          query = query.is("shipment_type", null);
        }

        if (node.criteria.incoterm?.id) {
          query = query.eq("incoterm", node.criteria.incoterm.id);
        } else {
          query = query.is("incoterm", null);
        }

        if (node.criteria.originPort?.id) {
          query = query.eq("origin_port", node.criteria.originPort.id);
        } else {
          query = query.is("origin_port", null);
        }

        if (node.criteria.destinationPort?.id) {
          query = query.eq(
            "destination_port",
            node.criteria.destinationPort.id
          );
        } else {
          query = query.is("destination_port", null);
        }

        const { data: existingRules, error: searchError } = await query;

        if (searchError) throw searchError;

        if (existingRules && existingRules.length > 0) {
          ruleId = existingRules[0].id;
        } else {
          message.error(
            "No rule found for this node. Please add a component first."
          );
          return;
        }
      }

      const { error } = await supabase
        .from("pricing_values")
        .update({ currency: currency })
        .eq("pricing_rule_id", ruleId)
        .eq("pricing_component_id", parseInt(componentId));

      if (error) throw error;

      // Update local state for immediate feedback
      setSelectedNodeData((prev) => {
        if (!prev) return prev;
        const updatedComponents = prev.components.map((c) =>
          c.id === componentId ? { ...c, currency } : c
        );

        return {
          ...prev,
          components: updatedComponents,
        };
      });
    } catch (error) {
      console.error("Error updating component currency:", error);
      message.error("Failed to update component currency");
    }
  };

  // Helper function to describe what node is selected
  const formatEuro = (amount: number): string => {
    return `€${amount.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const isComponentInherited = (
    componentId: string,
    node: PricingNode
  ): boolean => {
    // Check if the component is in the node's own components vs inherited
    const ownComponentIds = node.components.map((c) => c.id);
    return !ownComponentIds.includes(componentId);
  };

  const getInheritedComponentDetails = (node: PricingNode) => {
    // This would require building the inheritance chain to show exactly which components come from which parent rules
    // For now, we'll show that this node inherits from parents
    const inheritedComponents: Array<{
      componentName: string;
      value: number;
      currency: string;
      fromRule: string;
    }> = [];

    // In a real implementation, we'd traverse up the inheritance chain
    // and collect components from each parent rule
    return inheritedComponents;
  };

  const getNodeDescription = (node: PricingNode): string => {
    const parts: string[] = [];

    if (node.criteria.transportType) {
      parts.push(node.criteria.transportType.name);
    }
    if (node.criteria.shipmentType) {
      parts.push(node.criteria.shipmentType.name);
    }
    if (node.criteria.incoterm) {
      parts.push(node.criteria.incoterm.name);
    }
    if (node.criteria.containerSize) {
      parts.push(node.criteria.containerSize.name);
    }
    if (node.criteria.originPort) {
      parts.push(`From ${node.criteria.originPort.name}`);
    }
    if (node.criteria.destinationPort) {
      parts.push(`To ${node.criteria.destinationPort.name}`);
    }

    return parts.length > 0 ? parts.join(" → ") : "Root Level";
  };

  // Helper function to get available actions based on node type
  const getAvailableActionsForNode = (
    node: PricingNode
  ): Array<{ key: string; label: string; primary?: boolean }> => {
    const actions: Array<{ key: string; label: string; primary?: boolean }> =
      [];

    // Check if this is a header node (no actions for headers)
    if (node.id.startsWith("header-")) {
      return [];
    }

    // Based on current node criteria, determine what can be added next
    const criteria = node.criteria;

    // If no transport type, can add transport type
    if (!criteria.transportType) {
      actions.push({
        key: "add-transport",
        label: "📦 Add Transport Type",
        primary: true,
      });
    }
    // If transport type but no shipment type
    else if (!criteria.shipmentType) {
      actions.push({
        key: "add-shipment",
        label: "📋 Add Booking Type",
        primary: true,
      });
    }
    // If shipment type but no incoterm
    else if (!criteria.incoterm) {
      actions.push({
        key: "add-incoterm",
        label: "📄 Add Incoterm",
        primary: true,
      });
    }
    // If incoterm but seafreight and no container size
    else if (
      criteria.transportType.name.toLowerCase() === "seafreight" &&
      !criteria.containerSize
    ) {
      actions.push({
        key: "add-container",
        label: "📦 Add Container Size",
        primary: true,
      });
    }
    // If we can add origin port
    else if (!criteria.originPort) {
      actions.push({
        key: "add-origin",
        label: "🛫 Add Origin Port",
        primary: true,
      });
    }
    // If origin but no destination
    else if (!criteria.destinationPort) {
      actions.push({
        key: "add-destination",
        label: "🛬 Add Destination Port",
        primary: true,
      });
    }

    // Always allow adding components if it's not a header
    if (!node.id.startsWith("header-")) {
      actions.push({
        key: "add-component",
        label: "⚙️ Add Pricing Components",
      });
    }

    return actions;
  };

  const handleFormSubmit = async () => {
    try {
      const values = await form.validateFields();

      // Create rule name based on form values
      const transportTypeName =
        values.transportType === "1" ? "Airfreight" : "Seafreight";
      const originPortName = originPorts.find(
        (p) => p.id.toString() === values.originPort
      )?.name;
      const destinationPortName = destinationPorts.find(
        (p) => p.id.toString() === values.destinationPort
      )?.name;

      const ruleName = `${transportTypeName} - ${
        originPortName ? `Origin ${originPortName}` : "All Origins"
      } to ${
        destinationPortName
          ? `Destination ${destinationPortName}`
          : "All Destinations"
      }`;

      // Insert rule into database
      const { data: ruleData, error: ruleError } = await supabase
        .from("pricing_rules")
        .insert({
          name: ruleName,
          transport_type: values.transportType
            ? parseInt(values.transportType)
            : null,
          origin_port: values.originPort ? parseInt(values.originPort) : null,
          destination_port: values.destinationPort
            ? parseInt(values.destinationPort)
            : null,
          shipment_type: values.shipmentType
            ? parseInt(values.shipmentType)
            : null,
          incoterm: values.incoterm ? parseInt(values.incoterm) : null,
          is_active: true,
          valid_from: new Date().toISOString(),
        })
        .select();

      if (ruleError) throw ruleError;

      const createdRuleId = ruleData[0].id;

      // Insert component values if any components are selected
      if (selectedComponents.size > 0) {
        const componentValues = Array.from(selectedComponents).map(
          (componentId) => {
            const componentValue = values[`value-${componentId}`];
            const componentCurrency =
              values[`currency-${componentId}`] || "EUR";
            const includedProducts =
              values[`included_products-${componentId}`] || 0;
            const extraProductCost =
              values[`extra_product_cost-${componentId}`] || null;

            return {
              pricing_rule_id: createdRuleId,
              pricing_component_id: parseInt(componentId),
              value: componentValue,
              currency: componentCurrency,
              included_products: includedProducts,
              extra_product_cost: extraProductCost,
            };
          }
        );

        const { error: valuesError } = await supabase
          .from("pricing_values")
          .insert(componentValues);

        if (valuesError) throw valuesError;
      }

      message.success(
        `Rule "${ruleName}" created successfully with ${selectedComponents.size} components!`
      );
      setModalVisible(false);
      resetComponentSelection();

      // Reload the pricing data to show the new rule
      await loadPricingData();
    } catch (error) {
      console.error("Error creating rule:", error);
      message.error("Failed to create rule. Please try again.");
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Row gutter={24}>
        {/* Tree View */}
        <Col span={14}>
          <Card
            title={
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Title level={4} style={{ margin: 0 }}>
                    Pricing Tree
                  </Title>
                  <Button
                    type="text"
                    size="small"
                    icon={<InfoCircleOutlined />}
                    onClick={() => setHelpModalVisible(true)}
                    style={{ color: "#1890ff" }}
                  >
                    How it works
                  </Button>
                </div>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleAddRootRule}
                >
                  Add Transport Type
                </Button>
              </div>
            }
            size="small"
          >
            {loading ? (
              <div style={{ textAlign: "center", padding: "40px" }}>
                <Spin size="large" />
                <div style={{ marginTop: 16 }}>Loading pricing data...</div>
              </div>
            ) : (
              <Tree
                treeData={treeData}
                expandedKeys={expandedKeys}
                selectedKeys={selectedKeys}
                onExpand={(keys) => setExpandedKeys(keys as string[])}
                onSelect={(keys, info) => {
                  // Get the clicked node directly
                  const clickedNode = info.node as PricingNode;

                  // If it's a header node, just toggle expansion
                  if (clickedNode.selectable === false) {
                    const nodeKey = clickedNode.key;
                    const newExpandedKeys = expandedKeys.includes(nodeKey)
                      ? expandedKeys.filter((k) => k !== nodeKey)
                      : [...expandedKeys, nodeKey];
                    setExpandedKeys(newExpandedKeys);
                    return;
                  }

                  // Only allow selection of nodes that have specific criteria (actionable leaf nodes)
                  // These are nodes that represent actual pricing rules, not folder-level groupings
                  const isActionableNode =
                    clickedNode.nodeType === "specific" ||
                    (clickedNode.nodeType === "folder" &&
                      (clickedNode.criteria.transportType || // Transport types like "Airfreight"
                        clickedNode.criteria.destinationPort || // Has destination port (most specific)
                        clickedNode.criteria.originPort || // Has origin port
                        clickedNode.criteria.incoterm || // Has incoterm
                        clickedNode.criteria.containerSize)); // Has container size

                  if (!isActionableNode) {
                    // If it's not actionable, just expand/collapse like a folder
                    const nodeKey = clickedNode.key;
                    const newExpandedKeys = expandedKeys.includes(nodeKey)
                      ? expandedKeys.filter((k) => k !== nodeKey)
                      : [...expandedKeys, nodeKey];
                    setExpandedKeys(newExpandedKeys);
                    message.info(
                      "This node is for organization only. Select a specific route to manage components."
                    );
                    return;
                  }

                  // For actionable nodes, select them
                  setSelectedKeys([clickedNode.key]);
                  setSelectedNodeData(clickedNode);
                }}
                showLine={{ showLeafIcon: false }}
                blockNode
                style={{
                  backgroundColor: "#fafafa",
                  border: "1px solid #f0f0f0",
                  borderRadius: "8px",
                  padding: "16px",
                }}
              />
            )}
          </Card>
        </Col>

        {/* Details Panel */}
        <Col span={10}>
          <Card title="Rule Details" size="small">
            {selectedNodeData ? (
              <div>
                <Title level={5}>
                  {selectedNodeData.nodeType === "folder"
                    ? `📁 ${
                        selectedNodeData.criteria.transportType?.name ||
                        "Folder"
                      }`
                    : `📄 ${
                        selectedNodeData.criteria.transportType?.name || ""
                      } ${
                        selectedNodeData.criteria.originPort
                          ? `from ${selectedNodeData.criteria.originPort.name}`
                          : ""
                      } ${
                        selectedNodeData.criteria.destinationPort
                          ? `to ${selectedNodeData.criteria.destinationPort.name}`
                          : ""
                      }`.trim()}
                </Title>
                <Divider />

                <div style={{ marginBottom: 16 }}>
                  <Text strong>Criteria:</Text>
                  <div style={{ marginTop: 8 }}>
                    {selectedNodeData.criteria.transportType && (
                      <Tag color="blue">
                        {selectedNodeData.criteria.transportType.name}
                      </Tag>
                    )}
                    {selectedNodeData.criteria.originPort && (
                      <Tag color="green">
                        From: {selectedNodeData.criteria.originPort.name}
                      </Tag>
                    )}
                    {selectedNodeData.criteria.destinationPort && (
                      <Tag color="orange">
                        To: {selectedNodeData.criteria.destinationPort.name}
                      </Tag>
                    )}
                    {selectedNodeData.criteria.shipmentType && (
                      <Tag color="purple">
                        {selectedNodeData.criteria.shipmentType.name}
                      </Tag>
                    )}
                    {selectedNodeData.criteria.incoterm && (
                      <Tag color="cyan">
                        {selectedNodeData.criteria.incoterm.name}
                      </Tag>
                    )}
                    {!selectedNodeData.criteria.transportType &&
                      !selectedNodeData.criteria.originPort &&
                      !selectedNodeData.criteria.destinationPort &&
                      selectedNodeData.nodeType === "folder" && (
                        <Tag color="default">All Rules</Tag>
                      )}
                  </div>
                </div>

                {/* Show only this rule's own components, not inherited ones */}
                {selectedNodeData.components.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <Text strong>This Rule's Components:</Text>
                    <div
                      style={{
                        marginTop: 8,
                        background: "#f9f9f9",
                        padding: 12,
                        borderRadius: 6,
                      }}
                    >
                      {/* Show all components that belong to this specific rule */}
                      {selectedNodeData.components.map((component, index) => (
                        <div
                          key={`node-component-${component.id}-${index}`}
                          style={{
                            marginBottom: 12,
                            padding: "12px",
                            backgroundColor: "#ffffff",
                            borderRadius: "6px",
                            border: "1px solid #e8e8e8",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              marginBottom: "8px",
                            }}
                          >
                            <div style={{ flex: 1 }}>
                              <Text strong style={{ fontSize: "14px" }}>
                                {component.name}
                              </Text>
                              <div style={{ marginTop: "4px" }}>
                                {component.componentType && (
                                  <Tag color="blue">
                                    {component.componentType}
                                  </Tag>
                                )}
                                {component.calculationType && (
                                  <Tag color="orange">
                                    {component.calculationType}
                                  </Tag>
                                )}
                              </div>
                            </div>

                            {/* Editable value and currency section */}
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                }}
                              >
                                <span
                                  style={{ fontSize: "12px", color: "#666" }}
                                >
                                  {component.calculationType === "percentage"
                                    ? "%"
                                    : component.currency || "EUR"}
                                </span>
                                <input
                                  type="number"
                                  step={
                                    component.calculationType === "percentage"
                                      ? "0.1"
                                      : "0.01"
                                  }
                                  min="0"
                                  value={
                                    component.value === 0 ? "" : component.value
                                  }
                                  placeholder="0.00"
                                  onChange={(e) => {
                                    const newValue =
                                      e.target.value === ""
                                        ? 0
                                        : parseFloat(e.target.value) || 0;
                                    if (selectedNodeData) {
                                      handleComponentValueChange(
                                        component.id,
                                        newValue,
                                        selectedNodeData
                                      );
                                    }
                                  }}
                                  style={{
                                    width: "80px",
                                    padding: "4px 6px",
                                    border: "1px solid #d9d9d9",
                                    borderRadius: "4px",
                                    fontSize: "13px",
                                    textAlign: "right",
                                  }}
                                />
                                {component.calculationType !== "percentage" && (
                                  <select
                                    value={component.currency || "EUR"}
                                    onChange={(e) => {
                                      if (selectedNodeData) {
                                        handleComponentCurrencyChange(
                                          component.id,
                                          e.target.value,
                                          selectedNodeData
                                        );
                                      }
                                    }}
                                    style={{
                                      padding: "4px 6px",
                                      border: "1px solid #d9d9d9",
                                      borderRadius: "4px",
                                      fontSize: "12px",
                                      minWidth: "55px",
                                    }}
                                  >
                                    <option value="EUR">EUR</option>
                                    <option value="USD">USD</option>
                                    <option value="GBP">GBP</option>
                                    <option value="CNY">CNY</option>
                                  </select>
                                )}
                              </div>

                              {/* Remove button */}
                              <Button
                                type="text"
                                danger
                                size="small"
                                icon={
                                  <DeleteOutlined
                                    style={{ fontSize: "12px" }}
                                  />
                                }
                                onClick={() => {
                                  if (selectedNodeData) {
                                    handleComponentToggleInline(
                                      component.id,
                                      false,
                                      selectedNodeData
                                    );
                                  }
                                }}
                                style={{
                                  padding: "2px 4px",
                                  height: "24px",
                                  width: "24px",
                                }}
                                title="Remove component"
                              />
                            </div>
                          </div>

                          {/* Additional details for specific calculation types */}
                          {(component.includedProducts ||
                            component.extraProductSOC) && (
                            <div
                              style={{
                                fontSize: "11px",
                                color: "#666",
                                paddingTop: "4px",
                                borderTop: "1px solid #f0f0f0",
                                marginTop: "4px",
                              }}
                            >
                              {component.includedProducts && (
                                <div>
                                  Included Products:{" "}
                                  {component.includedProducts}
                                </div>
                              )}
                              {component.extraProductSOC && (
                                <div>
                                  Extra Product SOC: {component.extraProductSOC}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}

                      {/* Inherited components summary */}
                      {selectedNodeData.inheritedTotal > 0 && (
                        <div
                          style={{
                            marginTop: 16,
                            paddingTop: 12,
                            borderTop: "1px solid #e8e8e8",
                          }}
                        >
                          <Text
                            type="secondary"
                            style={{ fontStyle: "italic", fontSize: "12px" }}
                          >
                            Inherited from parent rules:
                          </Text>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              marginTop: 4,
                              fontSize: "12px",
                            }}
                          >
                            <Text type="secondary">
                              • Components from parent rules
                            </Text>
                            <Text type="secondary" strong>
                              {formatEuro(selectedNodeData.inheritedTotal)}
                            </Text>
                          </div>
                        </div>
                      )}

                      <Divider style={{ margin: "12px 0" }} />
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Text strong style={{ fontSize: "16px" }}>
                          Final Total:
                        </Text>
                        <Text
                          strong
                          style={{ color: "#52c41a", fontSize: "18px" }}
                        >
                          {formatEuro(selectedNodeData.finalTotal)}
                        </Text>
                      </div>
                    </div>
                  </div>
                )}

                <div style={{ marginTop: 16 }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 8,
                    }}
                  >
                    <Text strong>Available Components:</Text>
                    <Text type="secondary" style={{ fontSize: "12px" }}>
                      Check components to add them to this rule
                    </Text>
                  </div>
                  <div style={{ marginTop: 8 }}>
                    {availableComponents.length > 0 ? (
                      <div style={{ maxHeight: 200, overflowY: "auto" }}>
                        {availableComponents.map((component) => {
                          const isSelected = selectedNodeData.components.some(
                            (c) => c.id === component.id.toString()
                          );

                          return (
                            <div
                              key={`available-component-${component.id}`}
                              style={{
                                marginBottom: 8,
                                padding: 8,
                                border: "1px solid #f0f0f0",
                                borderRadius: 4,
                                backgroundColor: isSelected
                                  ? "#f6ffed"
                                  : "#fafafa",
                                cursor: "pointer",
                              }}
                              onClick={() => {
                                if (!isSelected && selectedNodeData) {
                                  handleComponentToggleInline(
                                    component.id.toString(),
                                    true,
                                    selectedNodeData
                                  );
                                }
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={(e) => {
                                      if (selectedNodeData) {
                                        handleComponentToggleInline(
                                          component.id.toString(),
                                          e.target.checked,
                                          selectedNodeData
                                        );
                                      }
                                    }}
                                    style={{ marginRight: 8 }}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                  <div>
                                    <Text strong style={{ fontSize: 12 }}>
                                      {component.name}
                                    </Text>
                                    <div
                                      style={{
                                        fontSize: 10,
                                        color: "#666",
                                        marginTop: 2,
                                      }}
                                    >
                                      {component.description} (
                                      {component.calculation_type})
                                    </div>
                                  </div>
                                </div>

                                {isSelected && <Tag color="green">Added</Tag>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        No components available. Add components in the
                        Components tab first.
                      </Text>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <Text type="secondary">
                Select a rule from the tree to see details
              </Text>
            )}
          </Card>
        </Col>
      </Row>

      {/* Selection Modal */}
      <Modal
        title={
          selectionModalType === "transport"
            ? "Select Transport Type"
            : selectionModalType === "booking"
            ? "Select Booking Type"
            : selectionModalType === "incoterm"
            ? "Select Incoterm"
            : selectionModalType === "origin"
            ? "Select Origin Port"
            : selectionModalType === "destination"
            ? "Select Destination Port"
            : "Select Option"
        }
        open={selectionModalVisible}
        afterOpenChange={(visible) =>
          console.log("Modal visibility changed:", visible)
        }
        onCancel={() => setSelectionModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setSelectionModalVisible(false)}>
            Cancel
          </Button>,
        ]}
        width={500}
      >
        {selectionModalType === "transport" && (
          <div style={{ maxHeight: "400px", overflowY: "auto" }}>
            <p style={{ marginBottom: "16px", color: "#666" }}>
              Choose a transport type to add a new pricing rule for:
            </p>

            {(() => {
              const availableTransportTypes = getAvailableTransportTypes();
              const hasAnyTransportRule = rulesData?.some(
                (rule) => rule.transport_type === null
              );

              if (availableTransportTypes.length === 0 && hasAnyTransportRule) {
                return (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "32px",
                      color: "#999",
                    }}
                  >
                    <p>All transport types already have pricing rules.</p>
                    <p>You currently have rules for:</p>
                    <ul style={{ marginTop: "8px", textAlign: "left" }}>
                      {hasAnyTransportRule && (
                        <li>🌍 All Transport Types (Global)</li>
                      )}
                      {transportTypes
                        .filter((tt) =>
                          rulesData?.some(
                            (rule) => rule.transport_type === tt.id
                          )
                        )
                        .map((tt) => (
                          <li key={tt.id}>✅ {tt.name}</li>
                        ))}
                    </ul>
                  </div>
                );
              }

              return (
                <>
                  {/* "All Transport Types" option - only show if not exists */}
                  {!hasAnyTransportRule && (
                    <div
                      style={{
                        padding: "12px",
                        border: "2px solid #52c41a",
                        borderRadius: "6px",
                        marginBottom: "12px",
                        cursor: "pointer",
                        transition: "all 0.2s",
                        backgroundColor: "#f6ffed",
                      }}
                      className="hover:bg-green-50"
                      onClick={() =>
                        handleCreateRule("transport", {
                          transportType: {
                            id: null,
                            name: "All Transport Types",
                          },
                        })
                      }
                    >
                      <div
                        style={{
                          fontWeight: "bold",
                          fontSize: "14px",
                          color: "#52c41a",
                        }}
                      >
                        🌍 All Transport Types
                      </div>
                      <div style={{ fontSize: "12px", color: "#666" }}>
                        Apply to airfreight, seafreight, and all other transport
                        methods
                      </div>
                    </div>
                  )}

                  {/* Only show available transport types */}
                  {availableTransportTypes.map((type) => (
                    <div
                      key={type.id}
                      style={{
                        padding: "12px",
                        border: "1px solid #d9d9d9",
                        borderRadius: "6px",
                        marginBottom: "8px",
                        cursor: "pointer",
                        transition: "all 0.2s",
                      }}
                      className="hover:bg-gray-50"
                      onClick={() =>
                        handleCreateRule("transport", { transportType: type })
                      }
                    >
                      <div style={{ fontWeight: "bold", fontSize: "14px" }}>
                        {type.name}
                      </div>
                      {type.abbreviation && (
                        <div style={{ fontSize: "12px", color: "#666" }}>
                          {type.abbreviation}
                        </div>
                      )}
                    </div>
                  ))}
                </>
              );
            })()}
          </div>
        )}

        {selectionModalType === "booking" && (
          <div style={{ maxHeight: "400px", overflowY: "auto" }}>
            <p style={{ marginBottom: "16px", color: "#666" }}>
              Choose a booking type to add:
            </p>

            {(() => {
              const transportTypeId = selectionModalParent?.transportType?.id;
              const availableBookingTypes =
                getAvailableBookingTypes(transportTypeId);
              const hasAnyBookingRule = rulesData?.some(
                (rule) =>
                  rule.transport_type === transportTypeId &&
                  rule.shipment_type === null
              );

              if (availableBookingTypes.length === 0 && hasAnyBookingRule) {
                return (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "32px",
                      color: "#999",
                    }}
                  >
                    <p>
                      All booking types already have rules for this transport
                      type.
                    </p>
                    <p>You currently have rules for:</p>
                    <ul style={{ marginTop: "8px", textAlign: "left" }}>
                      {hasAnyBookingRule && <li>📋 All Booking Types</li>}
                      {bookingTypes
                        .filter((bt) =>
                          rulesData?.some(
                            (rule) =>
                              rule.transport_type === transportTypeId &&
                              rule.shipment_type === bt.id
                          )
                        )
                        .map((bt) => (
                          <li key={bt.id}>✅ {bt.name}</li>
                        ))}
                    </ul>
                  </div>
                );
              }

              return (
                <>
                  {/* "All Booking Types" option - only show if not exists */}
                  {!hasAnyBookingRule && (
                    <div
                      style={{
                        padding: "12px",
                        border: "2px solid #52c41a",
                        borderRadius: "6px",
                        marginBottom: "12px",
                        cursor: "pointer",
                        transition: "all 0.2s",
                        backgroundColor: "#f6ffed",
                      }}
                      className="hover:bg-green-50"
                      onClick={() =>
                        handleCreateRule("booking", {
                          bookingType: { id: null, name: "All Booking Types" },
                          ...selectionModalParent,
                        })
                      }
                    >
                      <div
                        style={{
                          fontWeight: "bold",
                          fontSize: "14px",
                          color: "#52c41a",
                        }}
                      >
                        📋 All Booking Types
                      </div>
                      <div style={{ fontSize: "12px", color: "#666" }}>
                        Apply to LCL, FCL, and all other booking types
                      </div>
                    </div>
                  )}

                  {/* Only show available booking types */}
                  {availableBookingTypes.map((type) => (
                    <div
                      key={type.id}
                      style={{
                        padding: "12px",
                        border: "1px solid #d9d9d9",
                        borderRadius: "6px",
                        marginBottom: "8px",
                        cursor: "pointer",
                        transition: "all 0.2s",
                      }}
                      className="hover:bg-gray-50"
                      onClick={() =>
                        handleCreateRule("booking", {
                          bookingType: type,
                          ...selectionModalParent,
                        })
                      }
                    >
                      <div style={{ fontWeight: "bold", fontSize: "14px" }}>
                        {type.name}
                      </div>
                    </div>
                  ))}
                </>
              );
            })()}
          </div>
        )}

        {selectionModalType === "incoterm" && (
          <div style={{ maxHeight: "400px", overflowY: "auto" }}>
            <p style={{ marginBottom: "16px", color: "#666" }}>
              Choose an incoterm to add:
            </p>
            {(() => {
              const transportTypeId = selectionModalParent?.transportType?.id;
              const bookingTypeId = selectionModalParent?.bookingType?.id;
              const availableIncoterms = getAvailableIncoterms(
                transportTypeId,
                bookingTypeId
              );

              if (availableIncoterms.length === 0) {
                return (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "32px",
                      color: "#999",
                    }}
                  >
                    <p>
                      All incoterms already have rules for this combination.
                    </p>
                    <p>You currently have rules for:</p>
                    <ul style={{ marginTop: "8px", textAlign: "left" }}>
                      {incoterms
                        .filter((inc) =>
                          rulesData?.some(
                            (rule) =>
                              rule.transport_type === transportTypeId &&
                              rule.shipment_type === bookingTypeId &&
                              rule.incoterm === inc.id
                          )
                        )
                        .map((inc) => (
                          <li key={inc.id}>✅ {inc.name}</li>
                        ))}
                    </ul>
                  </div>
                );
              }

              return availableIncoterms.map((incoterm) => (
                <div
                  key={incoterm.id}
                  style={{
                    padding: "12px",
                    border: "1px solid #d9d9d9",
                    borderRadius: "6px",
                    marginBottom: "8px",
                    cursor: "pointer",
                    transition: "all 0.2s",
                  }}
                  className="hover:bg-gray-50"
                  onClick={() =>
                    handleCreateRule("incoterm", {
                      incoterm: incoterm,
                      ...selectionModalParent,
                    })
                  }
                >
                  <div style={{ fontWeight: "bold", fontSize: "14px" }}>
                    {incoterm.name}
                  </div>
                  <div style={{ fontSize: "12px", color: "#666" }}>
                    Incoterm: {incoterm.name}
                  </div>
                </div>
              ));
            })()}
          </div>
        )}

        {selectionModalType === "origin" && (
          <div style={{ maxHeight: "400px", overflowY: "auto" }}>
            <p style={{ marginBottom: "16px", color: "#666" }}>
              Choose an origin port to add:
            </p>

            {(() => {
              const transportTypeId = selectionModalParent?.transportType?.id;
              const bookingTypeId = selectionModalParent?.bookingType?.id;
              const incotermId = selectionModalParent?.incoterm?.id;
              const availableOriginPorts = getAvailableOriginPorts(
                transportTypeId,
                bookingTypeId,
                incotermId
              );
              const hasAnyOriginRule = rulesData?.some(
                (rule) =>
                  rule.transport_type === transportTypeId &&
                  rule.shipment_type === bookingTypeId &&
                  rule.incoterm === incotermId &&
                  rule.origin_port === null
              );

              if (availableOriginPorts.length === 0 && hasAnyOriginRule) {
                return (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "32px",
                      color: "#999",
                    }}
                  >
                    <p>
                      All origin ports already have rules for this combination.
                    </p>
                    <p>You currently have rules for:</p>
                    <ul style={{ marginTop: "8px", textAlign: "left" }}>
                      {hasAnyOriginRule && <li>🛫 All Origin Ports</li>}
                      {originPorts
                        .filter((port) =>
                          rulesData?.some(
                            (rule) =>
                              rule.transport_type === transportTypeId &&
                              rule.shipment_type === bookingTypeId &&
                              rule.incoterm === incotermId &&
                              rule.origin_port === port.id
                          )
                        )
                        .map((port) => (
                          <li key={port.id}>✅ {port.name}</li>
                        ))}
                    </ul>
                  </div>
                );
              }

              return (
                <>
                  {/* "All Origin Ports" option - only show if not exists */}
                  {!hasAnyOriginRule && (
                    <div
                      style={{
                        padding: "12px",
                        border: "2px solid #52c41a",
                        borderRadius: "6px",
                        marginBottom: "12px",
                        cursor: "pointer",
                        transition: "all 0.2s",
                        backgroundColor: "#f6ffed",
                      }}
                      className="hover:bg-green-50"
                      onClick={() =>
                        handleCreateRule("origin", {
                          originPort: { id: null, name: "All Origin Ports" },
                          ...selectionModalParent,
                        })
                      }
                    >
                      <div
                        style={{
                          fontWeight: "bold",
                          fontSize: "14px",
                          color: "#52c41a",
                        }}
                      >
                        🛫 All Origin Ports
                      </div>
                      <div style={{ fontSize: "12px", color: "#666" }}>
                        Apply to all origin ports worldwide
                      </div>
                    </div>
                  )}

                  {/* Only show available origin ports */}
                  {availableOriginPorts.map((port) => (
                    <div
                      key={port.id}
                      style={{
                        padding: "12px",
                        border: "1px solid #d9d9d9",
                        borderRadius: "6px",
                        marginBottom: "8px",
                        cursor: "pointer",
                        transition: "all 0.2s",
                      }}
                      className="hover:bg-gray-50"
                      onClick={() =>
                        handleCreateRule("origin", {
                          originPort: port,
                          ...selectionModalParent,
                        })
                      }
                    >
                      <div style={{ fontWeight: "bold", fontSize: "14px" }}>
                        {port.name}
                      </div>
                      {port.country && (
                        <div style={{ fontSize: "12px", color: "#666" }}>
                          {port.country}
                        </div>
                      )}
                    </div>
                  ))}
                </>
              );
            })()}
          </div>
        )}

        {selectionModalType === "destination" && (
          <div style={{ maxHeight: "400px", overflowY: "auto" }}>
            <p style={{ marginBottom: "16px", color: "#666" }}>
              Choose a destination port to add:
            </p>

            {(() => {
              const transportTypeId = selectionModalParent?.transportType?.id;
              const bookingTypeId = selectionModalParent?.bookingType?.id;
              const incotermId = selectionModalParent?.incoterm?.id;
              const originPortId = selectionModalParent?.originPort?.id;
              const availableDestinationPorts = getAvailableDestinationPorts(
                transportTypeId,
                bookingTypeId,
                incotermId,
                originPortId
              );
              const hasAnyDestinationRule = rulesData?.some(
                (rule) =>
                  rule.transport_type === transportTypeId &&
                  rule.shipment_type === bookingTypeId &&
                  rule.incoterm === incotermId &&
                  rule.origin_port === originPortId &&
                  rule.destination_port === null
              );

              if (
                availableDestinationPorts.length === 0 &&
                hasAnyDestinationRule
              ) {
                return (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "32px",
                      color: "#999",
                    }}
                  >
                    <p>
                      All destination ports already have rules for this
                      combination.
                    </p>
                    <p>You currently have rules for:</p>
                    <ul style={{ marginTop: "8px", textAlign: "left" }}>
                      {hasAnyDestinationRule && (
                        <li>🛬 All Destination Ports</li>
                      )}
                      {destinationPorts
                        .filter((port) =>
                          rulesData?.some(
                            (rule) =>
                              rule.transport_type === transportTypeId &&
                              rule.shipment_type === bookingTypeId &&
                              rule.incoterm === incotermId &&
                              rule.origin_port === originPortId &&
                              rule.destination_port === port.id
                          )
                        )
                        .map((port) => (
                          <li key={port.id}>✅ {port.name}</li>
                        ))}
                    </ul>
                  </div>
                );
              }

              return (
                <>
                  {/* "All Destination Ports" option - only show if not exists */}
                  {!hasAnyDestinationRule && (
                    <div
                      style={{
                        padding: "12px",
                        border: "2px solid #52c41a",
                        borderRadius: "6px",
                        marginBottom: "12px",
                        cursor: "pointer",
                        transition: "all 0.2s",
                        backgroundColor: "#f6ffed",
                      }}
                      className="hover:bg-green-50"
                      onClick={() =>
                        handleCreateRule("destination", {
                          destinationPort: {
                            id: null,
                            name: "All Destination Ports",
                          },
                          ...selectionModalParent,
                        })
                      }
                    >
                      <div
                        style={{
                          fontWeight: "bold",
                          fontSize: "14px",
                          color: "#52c41a",
                        }}
                      >
                        🛬 All Destination Ports
                      </div>
                      <div style={{ fontSize: "12px", color: "#666" }}>
                        Apply to all destination ports worldwide
                      </div>
                    </div>
                  )}

                  {/* Only show available destination ports */}
                  {availableDestinationPorts.map((port) => (
                    <div
                      key={port.id}
                      style={{
                        padding: "12px",
                        border: "1px solid #d9d9d9",
                        borderRadius: "6px",
                        marginBottom: "8px",
                        cursor: "pointer",
                        transition: "all 0.2s",
                      }}
                      className="hover:bg-gray-50"
                      onClick={() =>
                        handleCreateRule("destination", {
                          destinationPort: port,
                          ...selectionModalParent,
                        })
                      }
                    >
                      <div style={{ fontWeight: "bold", fontSize: "14px" }}>
                        {port.name}
                      </div>
                      {port.country && (
                        <div style={{ fontSize: "12px", color: "#666" }}>
                          {port.country}
                        </div>
                      )}
                    </div>
                  ))}
                </>
              );
            })()}
          </div>
        )}
      </Modal>

      {/* Help Modal */}
      <Modal
        title="🌳 How the Pricing Tree Works"
        open={helpModalVisible}
        onCancel={() => setHelpModalVisible(false)}
        footer={[
          <Button
            key="close"
            type="primary"
            onClick={() => setHelpModalVisible(false)}
          >
            Got it!
          </Button>,
        ]}
        width={600}
      >
        <div style={{ fontSize: "16px", lineHeight: "1.6" }}>
          <div style={{ marginBottom: "20px" }}>
            <Text strong style={{ fontSize: "18px", color: "#1890ff" }}>
              🎯 What is this tool?
            </Text>
            <div style={{ marginTop: "8px" }}>
              This tool lets you set pricing for different shipping routes.
              Think of it like building a price list that gets more specific as
              you go down the tree.
            </div>
          </div>

          <div style={{ marginBottom: "20px" }}>
            <Text strong style={{ fontSize: "18px", color: "#52c41a" }}>
              🏗️ How does inheritance work?
            </Text>
            <div style={{ marginTop: "8px", marginBottom: "12px" }}>
              Each level <strong>automatically adds</strong> to the levels above
              it:
            </div>
            <div
              style={{
                background: "#f6ffed",
                border: "1px solid #b7eb8f",
                borderRadius: "6px",
                padding: "12px",
                fontFamily: "monospace",
                fontSize: "14px",
              }}
            >
              <div>• 🌍 Any Transport: 50€ handling</div>
              <div>
                • ✈️ Airfreight: +75€ air fees = <strong>125€ total</strong>
              </div>
              <div>
                • 📋 LCL booking: +25€ consolidation ={" "}
                <strong>150€ total</strong>
              </div>
              <div>
                • 📄 FOB terms: +20€ documentation = <strong>170€ total</strong>
              </div>
              <div
                style={{
                  marginTop: "8px",
                  fontWeight: "bold",
                  color: "#389e0d",
                }}
              >
                → Final price: 170€ for "Airfreight LCL FOB"
              </div>
            </div>
          </div>

          <div style={{ marginBottom: "20px" }}>
            <Text strong style={{ fontSize: "18px", color: "#fa8c16" }}>
              ➕ How to add new rules?
            </Text>
            <div style={{ marginTop: "8px" }}>
              Look for the <Tag color="blue">Add</Tag> buttons next to each item
              in the tree. They show exactly what you can add:
            </div>
            <div style={{ marginTop: "8px", paddingLeft: "16px" }}>
              • On Airfreight → "Add Booking Type" (like LCL, FCL)
              <br />
              • On LCL → "Add Incoterm" (like FOB, CIF)
              <br />• On FOB → "Add Origin Port" (like Shanghai, Rotterdam)
            </div>
          </div>

          <div>
            <Text strong style={{ fontSize: "18px", color: "#722ed1" }}>
              💡 Pro tip
            </Text>
            <div style={{ marginTop: "8px" }}>
              Start broad (like "Any Transport Type") for costs that apply to
              everything, then get specific (like "Shanghai to Rotterdam") for
              route-specific costs.
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default TreePricingTab;
